(() => {
  "use strict";

  class GeneralToxinsCalculator extends HTMLElement {
    connectedCallback() {
      if (this.__gtcInitialized) return;
      this.__gtcInitialized = true;

      const root = this;
      const nativeDocument = window.document;

      const GTC_CONFIG = {
        vetChatUrl: String(root.dataset.vetChatUrl || "").trim(),
        emergencyVetUrl: String(root.dataset.emergencyVetUrl || "").trim()
      };

      const document = {
        getElementById(id) {
          return root.querySelector(`#${CSS.escape(id)}`);
        },
        querySelector(selector) {
          return root.querySelector(selector);
        },
        querySelectorAll(selector) {
          return root.querySelectorAll(selector);
        },
        createElement: nativeDocument.createElement.bind(nativeDocument),
        execCommand: nativeDocument.execCommand
          ? nativeDocument.execCommand.bind(nativeDocument)
          : () => false,
        body: nativeDocument.body
      };

      /* ============================================================
         ASK A VET™ General Toxins Calculator
         Data model: each toxin defines per-species thresholds in a
         dose unit (mg/kg by default). computeDose(vals,kg) returns
         {value, unit} of the active principle per kg, or null when
         the toxin is qualitative (any exposure = act).
         ============================================================ */
      
      const SPECIES = [
        {id:'dog',   name:'Dog',    ico:'🐕'},
        {id:'cat',   name:'Cat',    ico:'🐈'},
        {id:'rabbit',name:'Rabbit', ico:'🐇'},
        {id:'ferret',name:'Ferret', ico:'🦡'},
        {id:'bird',  name:'Bird',   ico:'🦜'},
        {id:'horse', name:'Horse',  ico:'🐎'},
        {id:'cattle',name:'Cattle', ico:'🐄'},
        {id:'sheep', name:'Sheep',  ico:'🐑'},
        {id:'goat',  name:'Goat',   ico:'🐐'},
        {id:'pig',   name:'Pig',    ico:'🐖'},
      ];
      
      // Band order & display
      const BANDS = ['safe','mild','moderate','severe','critical'];
      const BAND_LABEL = {safe:'Below concern',mild:'Low / monitor',moderate:'Moderate',severe:'Severe',critical:'Critical'};
      const BAND_INDEX = {safe:0,mild:1,moderate:2,severe:3,critical:4};
      
      // Owner-facing verdicts per band
      const OWNER_VERDICT = {
        safe:'Below the selected reference threshold. This does not prove the exposure is safe.',
        mild:'Low-level exposure: watch closely.',
        moderate:'Enough to make them sick. Call a vet.',
        severe:'This is dangerous. Get to a vet now.',
        critical:'Emergency. Go now: every minute counts.'
      };
      const OWNER_ADVICE = {
        safe:'Keep an eye on them for the next 12–24 hours. If they start vomiting, go quiet, wobble, or seem off, call your vet. If you are unsure how much they ate, treat the higher amount as real.',
        mild:'Ring your vet or an animal poison line and tell them the amount and time. They may want to monitor at home or see the animal. Do not make them vomit unless a vet says to.',
        moderate:'Call your vet or emergency clinic now. Early treatment (within the first hour or two) is far more effective than waiting for symptoms. Have the product packaging ready.',
        severe:'Go to a vet or emergency clinic immediately. Do not wait for symptoms to appear. Bring the packaging and note the time it was eaten.',
        critical:'This is a life-threatening dose. Get to the nearest open vet right now and call ahead so they are ready. Bring the packaging or a photo of it.'
      };
      
      /* helper: build a mg-per-gram concentration select */
      function conc(id,label,options,hint){return {id,label,type:'select',options,hint};}
      function num(id,label,ph,hint){return {id,label,type:'number',placeholder:ph,hint};}
      
      /* Each toxin:
         {id,name,cat,aliases,doseUnit,active,
          inputs:[...], dose(vals,kg)-> {value,unit} | null,
          species:{ dog:{bands:[{min,band}], onset, treat, lethal, note} | {qualitative:true,band,msg} },
          ownerNote, clinicalNote }
         bands: sorted ascending by min; pick highest whose min<=dose. Below first min => safe.
      */
      
      const TOXINS = [
      /* ---------------- FOODS & KITCHEN ---------------- */
      {
        id:'chocolate', name:'Chocolate / cocoa', cat:'Food & kitchen',
        aliases:['theobromine','cocoa','cacao','dark chocolate','baking'],
        doseUnit:'mg/kg', active:'theobromine',
        inputs:[
          num('g','Amount of chocolate eaten (grams)','e.g. 50'),
          conc('type','Type of chocolate',[
            {label:'White chocolate',value:0.1},
            {label:'Milk chocolate',value:2.0},
            {label:'Semi-sweet / dark',value:5.5},
            {label:'Dark 70%+',value:8},
            {label:'Baking / unsweetened',value:16},
            {label:'Cocoa powder',value:20},
            {label:'Cacao nibs / mulch',value:20},
          ],'Theobromine content rises sharply with cocoa %.')
        ],
        dose:(v,kg)=>({value:(v.g*v.type)/kg, unit:'mg/kg theobromine'}),
        species:{
          dog:{bands:[{min:20,band:'mild'},{min:40,band:'moderate'},{min:60,band:'severe'},{min:100,band:'critical'}],
            onset:'2–12 h', treat:'Emesis if <1–2 h & asymptomatic; activated charcoal (repeat, enterohepatic recycling); IV fluids; manage tachyarrhythmia (β-blocker), sedation for CNS signs; urinary catheter.',
            lethal:'~100–200 mg/kg', note:'Signs: vomiting, restlessness, tachycardia, hyperthermia, tremor, seizures. Half-life long (~17 h).'},
          cat:{bands:[{min:20,band:'mild'},{min:40,band:'moderate'},{min:60,band:'severe'},{min:100,band:'critical'}],
            onset:'2–12 h', treat:'As per dog; cats rarely ingest but are sensitive.', note:'Cats less likely to eat chocolate but equally sensitive per kg.'},
          ferret:{bands:[{min:20,band:'moderate'},{min:40,band:'severe'}], onset:'2–12 h', treat:'As per dog.', note:'Treat as sensitive; limited data.'},
        },
        ownerNote:'It is the cocoa that matters, not the sugar. A block of dark or baking chocolate is far worse than the same weight of milk chocolate.'
      },
      {
        id:'xylitol', name:'Xylitol (sugar-free sweetener)', cat:'Food & kitchen',
        aliases:['birch sugar','sugar free gum','sweetener','sugarless','e967'],
        doseUnit:'mg/kg', active:'xylitol',
        inputs:[
          num('g','Amount of product eaten (grams)','e.g. 10'),
          conc('type','Product type (est. xylitol per gram)',[
            {label:'Pure xylitol / birch sugar powder',value:1000},
            {label:'Sugar-free gum / mints (high)',value:400},
            {label:'Sugar-free gum / mints (typical)',value:200},
            {label:'Sugar-free baked goods',value:50},
            {label:'Xylitol peanut butter',value:30},
          ],'Content varies hugely between brands: if unsure, choose the higher option.')
        ],
        dose:(v,kg)=>({value:(v.g*v.type)/kg, unit:'mg/kg xylitol'}),
        species:{
          dog:{bands:[{min:100,band:'moderate'},{min:500,band:'severe'},{min:1000,band:'critical'}],
            onset:'Hypoglycaemia 30–60 min (can be delayed with gum); liver injury 24–72 h',
            treat:'Do NOT wait for signs. Check blood glucose; dextrose supplementation; monitor liver enzymes & coags for 72 h; IV fluids, hepatoprotectants (SAMe/NAC).',
            lethal:'Hepatic failure risk >0.5 g/kg', note:'>100 mg/kg = hypoglycaemia; >500 mg/kg = acute hepatic necrosis. Emesis only if very recent and glucose stable.'},
          cat:{bands:[{min:500,band:'mild'}], onset:'Uncertain', treat:'Monitor glucose; toxicosis not well documented in cats.',
            note:'Cats appear far less sensitive; still avoid and monitor.'},
          ferret:{bands:[{min:100,band:'moderate'},{min:500,band:'severe'}], onset:'30–60 min', treat:'As per dog.', note:'Assume dog-like sensitivity.'},
        },
        ownerNote:'Xylitol is in more than chewing gum: some peanut butters, toothpaste, mints, protein bars and baking sweeteners. Check the label if a dog raids the pantry.'
      },
      {
        id:'grapes', name:'Grapes / raisins / sultanas', cat:'Food & kitchen',
        aliases:['currants','raisin','sultana','grape'],
        species:{
          dog:{qualitative:true, band:'severe', msg:'There is no known safe amount. Idiosyncratic acute kidney injury can occur even from a small handful, and you cannot predict which dog reacts.',
            onset:'Vomiting within 24 h; kidney injury 24–72 h', treat:'Decontaminate (emesis + activated charcoal if recent), then aggressive IV fluid diuresis 48–72 h with renal monitoring (urea, creatinine, SDMA, urine output).'},
          ferret:{qualitative:true, band:'moderate', msg:'Not well documented in ferrets; treat with caution and call a vet.'},
        },
        ownerNote:'This includes raisins, sultanas and currants: which are concentrated, so even a few in a scone or hot cross bun count. Don\'t wait to see if your dog is one of the sensitive ones.'
      },
      {
        id:'allium', name:'Onion / garlic / chives / leek', cat:'Food & kitchen',
        aliases:['allium','onion','garlic','chives','leek','shallot','onion powder'],
        doseUnit:'g/kg', active:'onion-equivalent',
        inputs:[
          num('g','Amount eaten (grams)','e.g. 30'),
          conc('type','What was it? (potency vs raw onion)',[
            {label:'Raw or cooked onion',value:1},
            {label:'Leek / shallot',value:1},
            {label:'Fresh garlic',value:5},
            {label:'Onion powder / dried',value:8},
            {label:'Garlic powder',value:40},
          ],'Garlic is ~5× and dried powders are far more concentrated than fresh.')
        ],
        dose:(v,kg)=>({value:(v.g*v.type)/kg, unit:'g/kg onion-equivalent'}),
        species:{
          dog:{bands:[{min:5,band:'moderate'},{min:15,band:'severe'},{min:30,band:'critical'}],
            onset:'Haemolytic anaemia 1–5 days', treat:'Decontaminate if recent; monitor PCV & for Heinz bodies over 3–5 days; supportive care ± transfusion if severe.',
            note:'Oxidative damage to red cells → Heinz-body haemolytic anaemia. Threshold ~5 g/kg onion.'},
          cat:{bands:[{min:1,band:'severe'},{min:5,band:'critical'}], onset:'1–5 days', treat:'As per dog; cats markedly more sensitive.',
            note:'Cats are far more sensitive: as little as ~1 g/kg (a spoon of onion-containing baby food can matter).'},
          ferret:{bands:[{min:1,band:'severe'}], onset:'1–5 days', treat:'As per cat.', note:'Highly sensitive; avoid entirely.'},
          cattle:{bands:[{min:5,band:'moderate'}], onset:'Days', treat:'Remove source; monitor for haemolysis.', note:'Large onion feeding causes haemolytic anaemia in ruminants.'},
          horse:{bands:[{min:5,band:'moderate'}], onset:'Days', treat:'Remove source; monitor.', note:'Wild/garden onion and garlic supplements can cause Heinz-body anaemia.'},
        },
        ownerNote:'Cooking does not make it safe. Watch out for hidden sources: gravy, stock, baby food, garlic bread and "natural" garlic supplements.'
      },
      {
        id:'macadamia', name:'Macadamia nuts', cat:'Food & kitchen',
        aliases:['macadamia','nut'],
        doseUnit:'g/kg', active:'macadamia',
        inputs:[ num('g','Amount of nuts eaten (grams)','e.g. 20') ],
        dose:(v,kg)=>({value:v.g/kg, unit:'g/kg nuts'}),
        species:{
          dog:{bands:[{min:2,band:'moderate'},{min:20,band:'severe'}],
            onset:'3–12 h', treat:'Usually self-limiting; decontaminate if large amount; supportive care, keep warm, analgesia. Resolves 24–48 h.',
            note:'Weakness/ataxia in hind limbs, tremor, hyperthermia, vomiting. Mechanism unknown. Rarely fatal but distressing.'},
        },
        ownerNote:'Classic picture is a wobbly, weak dog on the back legs a few hours after nuts. Not usually deadly, but worth a vet call: and watch for chocolate-coated ones (double trouble).'
      },
      {
        id:'caffeine', name:'Caffeine (coffee, energy drinks, pills)', cat:'Food & kitchen',
        aliases:['coffee','espresso','energy drink','caffeine pills','tea','pre-workout'],
        doseUnit:'mg/kg', active:'caffeine',
        inputs:[
          num('mg','Caffeine ingested (mg)','e.g. 200'),
        ],
        dose:(v,kg)=>({value:v.mg/kg, unit:'mg/kg caffeine'}),
        species:{
          dog:{bands:[{min:20,band:'mild'},{min:40,band:'moderate'},{min:60,band:'severe'},{min:150,band:'critical'}],
            onset:'30 min – 2 h', treat:'Emesis/charcoal if recent; IV fluids, β-blocker for tachyarrhythmia, sedation, urinary catheter (reduce reabsorption).',
            lethal:'~140–150 mg/kg', note:'Reference points: brewed coffee ~80–100 mg/cup; energy drink ~80–160 mg; caffeine pill ~100–200 mg; espresso shot ~65 mg.'},
          cat:{bands:[{min:20,band:'mild'},{min:40,band:'moderate'},{min:60,band:'severe'},{min:150,band:'critical'}],
            onset:'30 min – 2 h', treat:'As per dog.', note:'Similar sensitivity to dogs.'},
        },
        ownerNote:'Used coffee grounds and tea bags in the bin are a common source. Pre-workout and fat-burner powders are extremely concentrated.'
      },
      {
        id:'ethanol', name:'Alcohol / ethanol', cat:'Food & kitchen',
        aliases:['alcohol','beer','wine','spirits','ethanol','bread dough','rum ball'],
        doseUnit:'mL/kg', active:'pure ethanol',
        inputs:[
          num('ml','Volume of drink swallowed (mL)','e.g. 30'),
          conc('abv','Strength (% alcohol)',[
            {label:'Beer ~5%',value:0.05},
            {label:'Wine ~12%',value:0.12},
            {label:'Fortified/liqueur ~20%',value:0.20},
            {label:'Spirits ~40%',value:0.40},
            {label:'Pure/hand sanitiser ~70%',value:0.70},
          ],'Also consider raw bread dough: it ferments to alcohol in the stomach.')
        ],
        dose:(v,kg)=>({value:(v.ml*v.abv)/kg, unit:'mL/kg pure ethanol'}),
        species:{
          dog:{bands:[{min:0.5,band:'mild'},{min:1.5,band:'moderate'},{min:3,band:'severe'},{min:5.5,band:'critical'}],
            onset:'30–60 min', treat:'Supportive: warmth, IV fluids, monitor blood glucose & acid-base, airway protection; ventilate if respiratory depression.',
            lethal:'~5.5–8 mL/kg pure ethanol', note:'Also caused by raw bread/pizza dough (ethanol + gastric bloat/GDV risk) and rotting fruit.'},
          cat:{bands:[{min:0.5,band:'moderate'},{min:1.5,band:'severe'},{min:4,band:'critical'}],
            onset:'30–60 min', treat:'As per dog; cats more sensitive.', note:'More sensitive than dogs.'},
        },
        ownerNote:'Rising bread dough is a sneaky one: it keeps fermenting inside the stomach and can also swell dangerously. Call a vet if a dog eats raw dough.'
      },
      {
        id:'salt', name:'Salt / play dough / sea water', cat:'Food & kitchen',
        aliases:['sodium','salt dough','playdough','play-doh','sea water','ice melt'],
        doseUnit:'g/kg', active:'sodium chloride',
        inputs:[ num('g','Amount of salt/salt-dough eaten (grams)','e.g. 15') ],
        dose:(v,kg)=>({value:v.g/kg, unit:'g/kg salt'}),
        species:{
          dog:{bands:[{min:2,band:'moderate'},{min:4,band:'severe'}],
            onset:'Hours', treat:'DO NOT correct sodium quickly: slow rehydration to avoid cerebral oedema; measure serum Na⁺; lower Na by ≤0.5 mmol/L/h.',
            lethal:'~4 g/kg', note:'Homemade salt dough ornaments and paintball ingestion are classic causes.'},
          cat:{bands:[{min:2,band:'moderate'},{min:4,band:'severe'}], onset:'Hours', treat:'As per dog.', note:'Same principle: correct sodium slowly.'},
          pig:{qualitative:true, band:'severe', msg:'In pigs, "salt poisoning" is usually water deprivation rather than a clean dose: pigs that lose water access then over-drink develop fatal brain swelling. Restore water SLOWLY and in small amounts.',
            onset:'1–5 days', treat:'Reintroduce water gradually (small frequent amounts); do not allow free-access rehydration; supportive care, control seizures. Correct sodium slowly.'},
        },
        ownerNote:'Never force-feed salt to make a pet vomit: that advice is dangerous and outdated. Salt-dough Christmas ornaments are a surprisingly common poisoning, and in pigs the real trigger is losing access to water.'
      },
      
      /* ---------------- HUMAN MEDICINES ---------------- */
      {
        id:'paracetamol', name:'Paracetamol / acetaminophen', cat:'Human medicines',
        aliases:['acetaminophen','tylenol','panadol','paracetamol'],
        doseUnit:'mg/kg', active:'paracetamol',
        inputs:[
          num('n','Number of tablets','e.g. 1'),
          conc('str','Tablet strength',[
            {label:'500 mg (standard)',value:500},
            {label:'665 mg (osteo/extended)',value:665},
            {label:'120 mg (paediatric)',value:120},
            {label:'1000 mg',value:1000},
          ],'')
        ],
        dose:(v,kg)=>({value:(v.n*v.str)/kg, unit:'mg/kg'}),
        species:{
          cat:{bands:[{min:10,band:'severe'},{min:50,band:'critical'}],
            onset:'1–4 h', treat:'EMERGENCY. N-acetylcysteine (NAC) antidote ASAP; oxygen for methaemoglobinaemia; SAMe, vitamin C, supportive care. Even ONE tablet can kill a cat.',
            lethal:'One 500 mg tablet can be fatal', note:'Cats lack glucuronidation: no safe dose. Methaemoglobinaemia (chocolate-brown blood, facial/paw oedema) + hepatic necrosis.'},
          dog:{bands:[{min:100,band:'moderate'},{min:200,band:'severe'},{min:500,band:'critical'}],
            onset:'Hepatic 24–48 h; MetHb higher doses', treat:'NAC antidote; decontaminate if recent; hepatoprotectants; oxygen if MetHb.',
            lethal:'>500 mg/kg', note:'Hepatotoxic >100 mg/kg; methaemoglobinaemia at higher doses.'},
          ferret:{bands:[{min:10,band:'severe'}], onset:'1–4 h', treat:'As per cat: treat as highly sensitive.', note:'Assume cat-like sensitivity.'},
        },
        ownerNote:'For cats this is a true never: never give paracetamol to a cat under any circumstance. If a cat gets one tablet, that is an emergency.'
      },
      {
        id:'ibuprofen', name:'Ibuprofen (NSAID)', cat:'Human medicines',
        aliases:['nsaid','advil','nurofen','brufen','ibuprofen'],
        doseUnit:'mg/kg', active:'ibuprofen',
        inputs:[
          num('n','Number of tablets','e.g. 2'),
          conc('str','Tablet strength',[
            {label:'200 mg',value:200},{label:'400 mg',value:400},{label:'100 mg (child)',value:100},{label:'600 mg',value:600},
          ],'')
        ],
        dose:(v,kg)=>({value:(v.n*v.str)/kg, unit:'mg/kg'}),
        species:{
          dog:{bands:[{min:25,band:'mild'},{min:50,band:'moderate'},{min:175,band:'severe'},{min:400,band:'critical'}],
            onset:'GI 2–6 h; renal 12–72 h; CNS high dose', treat:'Decontaminate; IV fluids 48 h for renal protection; GI protectants (misoprostol, omeprazole, sucralfate); monitor renal values.',
            lethal:'>400 mg/kg (CNS/renal)', note:'>50 GI ulceration; >175 renal injury; >400 CNS depression/seizures.'},
          cat:{bands:[{min:25,band:'moderate'},{min:50,band:'severe'},{min:200,band:'critical'}],
            onset:'As per dog', treat:'As per dog; cats ~2× more sensitive.', note:'Roughly twice as sensitive as dogs.'},
          ferret:{bands:[{min:25,band:'severe'}], onset:'2–6 h', treat:'Ferrets very sensitive; treat aggressively.', note:'Ferrets are highly sensitive to ibuprofen (CNS signs at low doses).'},
        },
        ownerNote:'Never reach for the human medicine cabinet to treat a pet\'s pain. Ibuprofen and naproxen have a very narrow safety margin in dogs and cats.'
      },
      {
        id:'naproxen', name:'Naproxen (NSAID)', cat:'Human medicines',
        aliases:['aleve','naprosyn','naproxen','nsaid'],
        doseUnit:'mg/kg', active:'naproxen',
        inputs:[
          num('n','Number of tablets','e.g. 1'),
          conc('str','Tablet strength',[{label:'220 mg',value:220},{label:'250 mg',value:250},{label:'500 mg',value:500}],'')
        ],
        dose:(v,kg)=>({value:(v.n*v.str)/kg, unit:'mg/kg'}),
        species:{
          dog:{bands:[{min:5,band:'moderate'},{min:25,band:'severe'},{min:50,band:'critical'}],
            onset:'GI 2–6 h; renal 12–72 h', treat:'Decontaminate; prolonged IV fluids; GI protectants; naproxen has a very long half-life in dogs: monitor for days.',
            lethal:'>50 mg/kg', note:'Far more toxic to dogs than ibuprofen; long half-life (~72 h).'},
          cat:{bands:[{min:5,band:'severe'}], onset:'As per dog', treat:'As per dog; highly sensitive.', note:'Very narrow margin.'},
        },
        ownerNote:'Naproxen is more dangerous per milligram than ibuprofen because it hangs around in a dog\'s system for days. Even one tablet in a small dog is a vet visit.'
      },
      {
        id:'aspirin', name:'Aspirin (salicylate)', cat:'Human medicines',
        aliases:['aspirin','salicylate','acetylsalicylic'],
        doseUnit:'mg/kg', active:'aspirin',
        inputs:[
          num('n','Number of tablets','e.g. 1'),
          conc('str','Tablet strength',[{label:'300 mg',value:300},{label:'100 mg (low-dose)',value:100},{label:'500 mg',value:500}],'')
        ],
        dose:(v,kg)=>({value:(v.n*v.str)/kg, unit:'mg/kg'}),
        species:{
          dog:{bands:[{min:25,band:'moderate'},{min:50,band:'severe'},{min:100,band:'critical'}],
            onset:'4–6 h', treat:'Decontaminate; IV fluids, correct acid-base, GI protectants; monitor for metabolic acidosis.',
            lethal:'>100 mg/kg', note:'GI ulceration, metabolic acidosis; long half-life.'},
          cat:{bands:[{min:20,band:'severe'},{min:40,band:'critical'}], onset:'Delayed', treat:'As per dog; very slow metabolism.',
            note:'Cats metabolise salicylate very slowly: highly sensitive, easy to overdose.'},
        },
        ownerNote:'Cats clear aspirin extremely slowly, so doses that would be trivial in a person accumulate dangerously.'
      },
      {
        id:'pseudoephedrine', name:'Pseudoephedrine / decongestants', cat:'Human medicines',
        aliases:['sudafed','decongestant','pseudoephedrine','phenylephrine','cold and flu'],
        doseUnit:'mg/kg', active:'pseudoephedrine',
        inputs:[
          num('n','Number of tablets/capsules','e.g. 1'),
          conc('str','Strength',[{label:'30 mg',value:30},{label:'60 mg',value:60},{label:'120 mg (SR)',value:120},{label:'240 mg (SR)',value:240}],'')
        ],
        dose:(v,kg)=>({value:(v.n*v.str)/kg, unit:'mg/kg'}),
        species:{
          dog:{bands:[{min:1,band:'mild'},{min:5,band:'moderate'},{min:10,band:'severe'},{min:12,band:'critical'}],
            onset:'30 min – 8 h (SR delayed)', treat:'Decontaminate; sedation (acepromazine/phenothiazine), β-blocker, cooling, IV fluids, monitor cardiac.',
            lethal:'~10–12 mg/kg', note:'Agitation, hypertension, tachycardia, hyperthermia, tremor. Slow-release forms prolong signs.'},
          cat:{bands:[{min:1,band:'moderate'},{min:5,band:'severe'}], onset:'As per dog', treat:'As per dog.', note:'Sensitive; sympathomimetic signs.'},
        },
        ownerNote:'Combination "cold & flu" tablets are a double hazard: they often pair a decongestant with paracetamol.'
      },
      {
        id:'adhd', name:'ADHD / amphetamine stimulants', cat:'Human medicines',
        aliases:['adderall','vyvanse','ritalin','methylphenidate','amphetamine','dexamphetamine'],
        doseUnit:'mg/kg', active:'amphetamine',
        inputs:[ num('mg','Total drug ingested (mg)','e.g. 20') ],
        dose:(v,kg)=>({value:v.mg/kg, unit:'mg/kg'}),
        species:{
          dog:{bands:[{min:1,band:'moderate'},{min:4,band:'severe'},{min:10,band:'critical'}],
            onset:'30 min – 2 h (XR delayed)', treat:'Decontaminate early; sedation (acepromazine, chlorpromazine: NOT further stimulants), cyproheptadine for serotonin signs, cooling, IV fluids, monitor cardiac/temperature.',
            lethal:'Variable; >10–20 mg/kg high risk', note:'Serotonin syndrome / sympathetic storm: agitation, hyperthermia, tremor, seizures.'},
          cat:{bands:[{min:1,band:'severe'}], onset:'As per dog', treat:'As per dog.', note:'Sensitive.'},
        },
        ownerNote:'A single dropped ADHD tablet can cause a dangerous stimulant reaction. Extended-release versions can delay and prolong signs, so early vet care matters.'
      },
      {
        id:'baclofen', name:'Baclofen (muscle relaxant)', cat:'Human medicines',
        aliases:['baclofen','muscle relaxant','lioresal'],
        doseUnit:'mg/kg', active:'baclofen',
        inputs:[ num('mg','Total ingested (mg)','e.g. 10') ],
        dose:(v,kg)=>({value:v.mg/kg, unit:'mg/kg'}),
        species:{
          dog:{bands:[{min:0.5,band:'moderate'},{min:1.3,band:'severe'},{min:8,band:'critical'}],
            onset:'30 min – 2 h', treat:'Decontaminate; intensive supportive care, ventilation for respiratory depression, sedation for agitation; ILE may help; often needs 24–48 h ICU.',
            lethal:'>8–10 mg/kg', note:'Vocalising, agitation then CNS/respiratory depression, coma. Narrow margin.'},
          cat:{bands:[{min:0.5,band:'severe'}], onset:'As per dog', treat:'As per dog.', note:'Sensitive.'},
        },
        ownerNote:'Baclofen has one of the narrowest margins of the common household tablets: even low doses need urgent care.'
      },
      {
        id:'iron', name:'Iron supplements', cat:'Human medicines',
        aliases:['ferrous','iron tablets','prenatal','multivitamin iron'],
        doseUnit:'mg/kg', active:'elemental iron',
        inputs:[ num('mg','Elemental iron ingested (mg)','e.g. 100') ],
        dose:(v,kg)=>({value:v.mg/kg, unit:'mg/kg elemental iron'}),
        species:{
          dog:{bands:[{min:20,band:'mild'},{min:60,band:'moderate'},{min:100,band:'severe'},{min:200,band:'critical'}],
            onset:'GI 0–6 h; shock/liver 6–48 h', treat:'Not adsorbed by charcoal; decontaminate; deferoxamine chelation for severe; IV fluids, GI protectants; monitor liver.',
            lethal:'>100–200 mg/kg elemental', note:'Note ELEMENTAL iron, not tablet weight. Prenatal vitamins are a common source.'},
          cat:{bands:[{min:20,band:'moderate'},{min:60,band:'severe'}], onset:'As per dog', treat:'As per dog.', note:'Similar principle.'},
        },
        ownerNote:'It is the elemental iron that counts, not the tablet weight: a 300 mg ferrous sulfate tablet is only ~60 mg elemental iron. Prenatal and kids\' gummy vitamins are common culprits.'
      },
      {
        id:'vitd', name:'Vitamin D supplements', cat:'Human medicines',
        aliases:['vitamin d','cholecalciferol','calcitriol','psoriasis cream','vitamin d3'],
        doseUnit:'IU/kg', active:'vitamin D3',
        inputs:[ num('iu','Total vitamin D ingested (IU)','e.g. 50000') ],
        dose:(v,kg)=>({value:v.iu/kg, unit:'IU/kg'}),
        species:{
          dog:{bands:[{min:1000,band:'moderate'},{min:5000,band:'severe'},{min:10000,band:'critical'}],
            onset:'12–72 h', treat:'Decontaminate; treat hypercalcaemia (IV fluids, furosemide, steroids, bisphosphonates); monitor Ca × P and renal values for days.',
            lethal:'>10,000 IU/kg', note:'Hypercalcaemia → renal failure & soft-tissue mineralisation. Also from psoriasis creams (calcipotriene): tiny amounts dangerous.'},
          cat:{bands:[{min:1000,band:'severe'}], onset:'12–72 h', treat:'As per dog.', note:'Sensitive.'},
        },
        ownerNote:'Psoriasis creams (calcipotriol) are deceptively potent: a dog licking a treated arm or the tube can get a serious dose.'
      },
      
      /* ---------------- RODENTICIDES & PEST ---------------- */
      {
        id:'anticoag', name:'Rat bait: anticoagulant', cat:'Rodenticides & pest',
        aliases:['warfarin','brodifacoum','bromadiolone','difenacoum','rat bait','mouse bait','rodenticide','difethialone'],
        doseUnit:'mg/kg', active:'anticoagulant',
        inputs:[
          num('g','Amount of bait eaten (grams)','e.g. 10'),
          conc('type','Active + concentration (typical mg/g)',[
            {label:'Brodifacoum 0.005%',value:0.05},
            {label:'Bromadiolone 0.005%',value:0.05},
            {label:'Difenacoum 0.005%',value:0.05},
            {label:'Difethialone 0.0025%',value:0.025},
            {label:'Warfarin 0.025%',value:0.25},
          ],'Most modern baits are ~0.005% (0.05 mg/g).')
        ],
        dose:(v,kg)=>({value:(v.g*v.type)/kg, unit:'mg/kg active'}),
        species:{
          dog:{bands:[{min:0.02,band:'moderate'},{min:0.1,band:'severe'},{min:0.25,band:'critical'}],
            onset:'Coagulopathy 2–5 days (delayed!)', treat:'Vitamin K1 antidote; if <2–4 h emesis. If exposure uncertain, check PT at 48–72 h and treat if prolonged. Second-generation baits need 3–4 weeks of Vit K1.',
            lethal:'Second-gen LD50 ~0.25 mg/kg', note:'Bleeding is DELAYED. A normal-looking dog today can bleed out in 3 days. When in doubt, treat / test PT.'},
          cat:{bands:[{min:0.02,band:'moderate'},{min:0.1,band:'severe'},{min:0.25,band:'critical'}], onset:'2–5 days', treat:'As per dog; cats often more resistant but treat the same.', note:'Cats relatively resistant but still treat/monitor.'},
        },
        ownerNote:'The scary part is the delay: there are no signs for a few days, then internal bleeding. If you even suspect your pet found rat bait, call your vet; the antidote (vitamin K1) works well when started early.'
      },
      {
        id:'bromethalin', name:'Rat bait: bromethalin', cat:'Rodenticides & pest',
        aliases:['bromethalin','rat bait','fastrac','neurotoxic rodenticide'],
        doseUnit:'mg/kg', active:'bromethalin',
        inputs:[
          num('g','Amount of bait eaten (grams)','e.g. 5'),
          conc('type','Concentration',[{label:'0.01% (0.1 mg/g)',value:0.1},{label:'0.025% (0.25 mg/g)',value:0.25}],'')
        ],
        dose:(v,kg)=>({value:(v.g*v.type)/kg, unit:'mg/kg'}),
        species:{
          dog:{bands:[{min:0.1,band:'moderate'},{min:0.5,band:'severe'},{min:2.5,band:'critical'}],
            onset:'2 h – 7 days', treat:'NO antidote. Aggressive early decontamination + repeated activated charcoal (enterohepatic recycling); manage cerebral oedema (mannitol); intensive supportive care.',
            lethal:'~2.5 mg/kg', note:'Neurotoxin → cerebral oedema. There is NO antidote, so early decontamination is everything.'},
          cat:{bands:[{min:0.05,band:'moderate'},{min:0.3,band:'severe'},{min:0.5,band:'critical'}],
            onset:'2 h – 7 days', treat:'As per dog; cats ~5× more sensitive.', lethal:'~0.5 mg/kg', note:'Cats considerably more sensitive than dogs.'},
        },
        ownerNote:'Not all rat baits are the same. Bromethalin has no antidote, so if you know or suspect the brand, tell your vet: it changes the whole treatment plan.'
      },
      {
        id:'cholecalciferol', name:'Rat bait: cholecalciferol (vitamin D3)', cat:'Rodenticides & pest',
        aliases:['cholecalciferol','vitamin d rat bait','d3 rodenticide','terad'],
        doseUnit:'mg/kg', active:'cholecalciferol',
        inputs:[
          num('g','Amount of bait eaten (grams)','e.g. 5'),
          conc('type','Concentration',[{label:'0.075% (0.75 mg/g)',value:0.75}],'')
        ],
        dose:(v,kg)=>({value:(v.g*v.type)/kg, unit:'mg/kg'}),
        species:{
          dog:{bands:[{min:0.1,band:'moderate'},{min:0.5,band:'severe'},{min:2,band:'critical'}],
            onset:'12–72 h', treat:'Decontaminate; treat hypercalcaemia aggressively (fluids, furosemide, steroids, bisphosphonates: pamidronate); monitor Ca/P & renal for 1–2 weeks.',
            lethal:'>2 mg/kg', note:'One of the harder rodenticides to treat: hypercalcaemia causes irreversible renal & tissue mineralisation.'},
          cat:{bands:[{min:0.1,band:'severe'}], onset:'12–72 h', treat:'As per dog.', note:'Sensitive.'},
        },
        ownerNote:'Cholecalciferol baits punch above their weight: a small amount can cause serious kidney damage, and treatment can run into thousands. Prevention beats cure here.'
      },
      {
        id:'metaldehyde', name:'Snail / slug bait (metaldehyde)', cat:'Rodenticides & pest',
        aliases:['metaldehyde','snail bait','slug bait','snail pellets','shake and rattle'],
        doseUnit:'mg/kg', active:'metaldehyde',
        inputs:[
          num('g','Amount of pellets eaten (grams)','e.g. 10'),
          conc('type','Concentration',[{label:'3% (30 mg/g) typical',value:30},{label:'5% (50 mg/g)',value:50},{label:'1.5% (15 mg/g)',value:15}],'')
        ],
        dose:(v,kg)=>({value:(v.g*v.type)/kg, unit:'mg/kg'}),
        species:{
          dog:{bands:[{min:30,band:'moderate'},{min:100,band:'severe'},{min:200,band:'critical'}],
            onset:'30 min – 3 h', treat:'"Shake and bake": tremors + hyperthermia. Control tremors/seizures (methocarbamol, diazepam, propofol/GA), active cooling, IV fluids, decontaminate under anaesthesia if convulsing.',
            lethal:'~200–400 mg/kg', note:'Rapid onset severe tremors, seizures and dangerous hyperthermia. A true rush-to-the-vet toxin.'},
          cat:{bands:[{min:30,band:'moderate'},{min:100,band:'severe'}], onset:'30 min – 3 h', treat:'As per dog.', note:'Sensitive; same picture.'},
          horse:{bands:[{min:30,band:'severe'}], onset:'Rapid', treat:'Sedation, cooling, supportive.', note:'Reported in horses accessing bait.'},
        },
        ownerNote:'Green or blue pellets in the garden shed are a red flag. Signs come on fast: twitching, tremors and overheating within an hour or two. Go straight to a vet.'
      },
      {
        id:'strychnine', name:'Strychnine bait', cat:'Rodenticides & pest',
        aliases:['strychnine','fox bait','vermin bait'],
        species:{
          dog:{qualitative:true, band:'critical', msg:'Extremely fast-acting and frequently fatal. Any suspected exposure is a dire emergency.',
            onset:'10–120 min', treat:'Control seizures aggressively (GA), minimise stimulation, decontaminate under anaesthesia, intensive supportive care. Guarded prognosis.'},
          cat:{qualitative:true, band:'critical', msg:'As per dog: life-threatening, act immediately.'},
          horse:{qualitative:true, band:'critical', msg:'Life-threatening; call immediately.'},
        },
        ownerNote:'These are illegal or restricted pesticides in most areas and are used in some regions for wild-dog and fox control. If you are near baiting programs, keep dogs muzzled or leashed and know where the nearest emergency vet is.'
      },
      {
        id:'fluoroacetate', name:'Sodium fluoroacetate / 1080 bait', cat:'Rodenticides & pest',
        aliases:['1080','sodium fluoroacetate','fluoroacetate','fox bait','wild dog bait','vermin bait'],
        species:{
          dog:{qualitative:true, band:'critical', msg:'1080 is rapidly absorbed and potentially fatal. Any suspected ingestion is an immediate emergency; do not wait for symptoms.',
            onset:'Usually 30 min – 3 h, sometimes longer', treat:'No routinely available specific antidote. Immediate veterinary decontamination when appropriate, seizure control, cardiac monitoring and intensive supportive care. Minimise stimulation and call ahead.'},
          cat:{qualitative:true, band:'critical', msg:'Potentially fatal. Seek emergency veterinary treatment immediately.', onset:'Minutes – hours', treat:'Urgent decontamination when appropriate and intensive supportive care; no routinely available specific antidote.'},
          horse:{qualitative:true, band:'critical', msg:'Potentially fatal. Seek emergency veterinary treatment immediately.', onset:'Minutes – hours', treat:'Urgent veterinary decontamination when appropriate and intensive supportive care.'},
        },
        ownerNote:'1080 and strychnine are different poisons and require different clinical thinking. Bring the bait packaging or a clear photo, but do not handle unknown bait with bare hands.'
      },
      
      /* ---------------- GARDEN, HOME & CHEMICALS ---------------- */
      {
        id:'ethyleneglycol', name:'Antifreeze / ethylene glycol', cat:'Garden, home & chemicals',
        aliases:['antifreeze','ethylene glycol','coolant','radiator fluid','screen wash'],
        doseUnit:'mL/kg', active:'pure ethylene glycol',
        inputs:[
          num('ml','Volume swallowed (mL)','e.g. 5'),
          conc('conc','Product concentration',[{label:'Concentrated antifreeze ~95%',value:0.95},{label:'Pre-mixed coolant ~50%',value:0.50}],'')
        ],
        dose:(v,kg)=>({value:(v.ml*v.conc)/kg, unit:'mL/kg pure EG'}),
        species:{
          dog:{bands:[{min:1,band:'severe'},{min:4.4,band:'critical'}],
            onset:'Wobbly 30 min – 12 h; kidney failure 24–72 h', treat:'TIME-CRITICAL antidote: fomepizole (or ethanol). Effective only if started very early (ideally <8 h). Once anuric renal failure sets in, prognosis is grave.',
            lethal:'~4.4–6.6 mL/kg (95%)', note:'Sweet taste attracts pets. Early treatment saves lives; late treatment rarely does.'},
          cat:{bands:[{min:0.5,band:'severe'},{min:1.4,band:'critical'}],
            onset:'As per dog', treat:'Antidote ASAP; cats need treatment within ~3 h to survive.', lethal:'~1.4 mL/kg', note:'Cats are exquisitely sensitive: as little as a teaspoon (a few licks off a paw) can be lethal.'},
        },
        ownerNote:'This is one of the most time-critical poisonings there is. The antidote works brilliantly early and barely at all late, so do not "wait and see": a few licks off a garage floor is enough for a cat.'
      },
      {
        id:'permethrin', name:'Permethrin / pyrethroid (dog flea spot-on on a cat)', cat:'Garden, home & chemicals',
        aliases:['permethrin','pyrethroid','pyrethrin','flea treatment','spot on','advantix','insecticide'],
        species:{
          cat:{qualitative:true, band:'critical', msg:'Applying a concentrated dog spot-on to a cat, or a cat grooming a treated dog, is a genuine emergency. Cats cannot metabolise permethrin.',
            onset:'Minutes – hours', treat:'Wash off with liquid dish soap + warm water; control tremors/seizures (methocarbamol is drug of choice, diazepam), IV lipid emulsion (ILE), thermoregulation, supportive care 24–72 h.'},
          dog:{qualitative:true, band:'mild', msg:'Dogs tolerate permethrin well at label doses. Only gross overdose causes signs (local tingling, occasional tremor). Bathe and monitor.',
            onset:'Hours', treat:'Usually mild in dogs; bathe with dish soap, monitor. Methocarbamol only if tremors appear.'},
          rabbit:{qualitative:true, band:'severe', msg:'Rabbits are sensitive: use only rabbit-safe products. Bathe off and seek advice.',
            onset:'Hours', treat:'Wash off; supportive care; control tremors.'},
        },
        ownerNote:'The classic disaster is a well-meaning owner putting a dog\'s flea treatment on a cat, or a freshly-treated dog snuggling a cat. If it happens, wash the cat with dish soap and get to a vet fast: tremors can escalate to seizures.'
      },
      {
        id:'organophosphate', name:'Organophosphate / carbamate insecticide', cat:'Garden, home & chemicals',
        aliases:['organophosphate','carbamate','malathion','diazinon','carbaryl','insecticide','chlorpyrifos','anticholinesterase'],
        species:{
          dog:{qualitative:true, band:'severe', msg:'Cholinesterase inhibitors: dose depends heavily on the specific product. Any significant exposure needs urgent care.',
            onset:'Minutes – hours', treat:'SLUDGE/DUMBELS signs. Atropine to effect for muscarinic signs; pralidoxime (2-PAM) for organophosphates (not carbamates); decontaminate, control seizures, respiratory support.'},
          cat:{qualitative:true, band:'severe', msg:'As per dog; cats sensitive.'},
          cattle:{qualitative:true, band:'severe', msg:'Common in livestock via dips/sprays; atropine + supportive.'},
          horse:{qualitative:true, band:'severe', msg:'As per others.'},
          sheep:{qualitative:true, band:'severe', msg:'Dips and sprays a common source; atropine + supportive.'},
          goat:{qualitative:true, band:'severe', msg:'As per sheep.'},
          pig:{qualitative:true, band:'severe', msg:'Access to treated crops or spilled product; atropine + supportive.'},
          rabbit:{qualitative:true, band:'severe', msg:'Very sensitive; wash off dermal exposure, atropine + supportive.'},
          bird:{qualitative:true, band:'severe', msg:'Birds highly sensitive; a common cause of bird poisoning.'},
        },
        ownerNote:'Think drooling, tearing, urinating, diarrhoea, tiny pupils and muscle twitching after contact with garden or farm insecticide. This is a call-now situation: there is an antidote (atropine).'
      },
      {
        id:'glyphosate', name:'Glyphosate herbicide (Roundup)', cat:'Garden, home & chemicals',
        aliases:['glyphosate','roundup','weed killer','herbicide'],
        species:{
          dog:{qualitative:true, band:'mild', msg:'Usually low toxicity once dried on foliage; the surfactant causes GI upset. Large direct ingestion of concentrate is more serious.',
            onset:'0–2 h', treat:'Dilute with water/food, anti-emetics, monitor; mostly self-limiting GI signs. Concentrate ingestion → supportive care.'},
          cat:{qualitative:true, band:'mild', msg:'As per dog; drooling and GI upset common.'},
        },
        ownerNote:'Once it has dried on the lawn, glyphosate is usually just a mild tummy-upset risk. Keep pets off treated areas until dry, and stop them drinking the concentrate.'
      },
      {
        id:'zinc', name:'Zinc (coins, bolts, zinc cream)', cat:'Garden, home & chemicals',
        aliases:['zinc','penny','coin','galvanised','nut and bolt','sunscreen','zinc oxide'],
        species:{
          dog:{qualitative:true, band:'severe', msg:'A swallowed zinc object (such as some zinc-core coins or galvanised nuts) can slowly dissolve in stomach acid and cause life-threatening haemolysis.',
            onset:'Days', treat:'Remove the object (endoscopy/surgery): this is the key step; treat haemolytic anaemia, IV fluids, transfusion if needed; chelation controversial.'},
          cat:{qualitative:true, band:'severe', msg:'As per dog.'},
          bird:{qualitative:true, band:'severe', msg:'Heavy-metal toxicosis: classic in birds chewing galvanised cage wire/toys.'},
        },
        ownerNote:'A dog that swallowed a coin and is now pale, jaundiced or passing dark urine needs urgent imaging: the metal object has to come out.'
      },
      {
        id:'leadmetal', name:'Lead (paint, sinkers, solder)', cat:'Garden, home & chemicals',
        aliases:['lead','paint chips','fishing sinker','solder','curtain weight','heavy metal'],
        species:{
          dog:{qualitative:true, band:'severe', msg:'Chronic or acute; GI + neurological signs. Common in pups chewing old painted surfaces.',
            onset:'Days – weeks', treat:'Remove source/object; chelation (succimer/CaEDTA); supportive care, seizure control.'},
          cat:{qualitative:true, band:'severe', msg:'As per dog.'},
          bird:{qualitative:true, band:'severe', msg:'Very common in birds; chelation + supportive care.'},
          cattle:{qualitative:true, band:'critical', msg:'Cattle are the classic lead-poisoning species (batteries, old paint, sump oil). Often fatal; notifiable in food animals: involve your vet immediately.'},
          horse:{qualitative:true, band:'severe', msg:'Chronic pasture contamination near smelters/batteries.'},
          sheep:{qualitative:true, band:'severe', msg:'Access to old batteries, paint or contaminated pasture.'},
          goat:{qualitative:true, band:'severe', msg:'As per sheep.'},
          pig:{qualitative:true, band:'severe', msg:'Chewing painted surfaces or contaminated feed; GI + neuro signs.'},
          rabbit:{qualitative:true, band:'severe', msg:'Chewing old painted hutches/wire; chelation + remove source.'},
        },
        ownerNote:'Old flaking paint (pre-1970s houses), fishing sinkers, curtain weights and lead-light solder are common sources, especially for chewing puppies and caged birds.'
      },
      {
        id:'battery', name:'Batteries (alkaline / button / lithium)', cat:'Garden, home & chemicals',
        aliases:['battery','button battery','lithium','alkaline','coin cell'],
        species:{
          dog:{qualitative:true, band:'severe', msg:'Chewed alkaline batteries cause caustic burns; a swallowed button/coin cell can burn through tissue in HOURS: a true emergency.',
            onset:'Immediate – hours', treat:'Do NOT induce vomiting (re-exposes to caustic). Radiograph to locate; button cells lodged in oesophagus need urgent removal; dilute with milk/water, GI protectants, monitor for perforation.'},
          cat:{qualitative:true, band:'severe', msg:'As per dog.'},
          bird:{qualitative:true, band:'severe', msg:'Caustic + heavy-metal risk.'},
        },
        ownerNote:'A swallowed button battery is a "go now" emergency: it can burn a hole through the gullet within hours. Do not make the pet vomit; get an x-ray.'
      },
      
      /* ---------------- CANNABIS & RECREATIONAL ---------------- */
      {
        id:'thc', name:'Cannabis / THC', cat:'Cannabis & recreational',
        aliases:['thc','marijuana','weed','edible','cannabis','pot','hash','dog ate weed'],
        doseUnit:'mg/kg', active:'THC',
        inputs:[ num('mg','Total THC ingested (mg)','e.g. 10') ],
        dose:(v,kg)=>({value:v.mg/kg, unit:'mg/kg THC'}),
        species:{
          dog:{bands:[{min:1.5,band:'mild'},{min:3,band:'moderate'},{min:9,band:'severe'}],
            onset:'30 min – 3 h (edibles slower)', treat:'Mostly supportive: quiet warm space, anti-emetics, IV fluids, monitor. ILE for severe. Watch for the toxic co-ingredient: chocolate, xylitol or raisins in edibles is often the bigger threat.',
            lethal:'Death from THC alone is very rare (>3 g/kg)', note:'Classic sign: sedated dog that startles and dribbles urine ("urinary incontinence + hyperaesthesia"). Butter/concentrates carry much higher doses.'},
          cat:{bands:[{min:1.5,band:'mild'},{min:3,band:'moderate'}], onset:'As per dog', treat:'As per dog.', note:'Similar; supportive.'},
        },
        ownerNote:'Be honest with your vet: nobody is calling the police, and knowing it is THC saves time and money. The real danger in an "edible" is often the chocolate, xylitol or raisins baked in with it.'
      },
      {
        id:'nicotine', name:'Nicotine (cigarettes, vapes, patches, pouches)', cat:'Cannabis & recreational',
        aliases:['nicotine','cigarette','vape','e-liquid','patch','nicotine gum','tobacco','pouch','snus'],
        doseUnit:'mg/kg', active:'nicotine',
        inputs:[
          num('mg','Nicotine ingested (mg)','e.g. 16'),
        ],
        dose:(v,kg)=>({value:v.mg/kg, unit:'mg/kg nicotine'}),
        species:{
          dog:{bands:[{min:0.5,band:'moderate'},{min:1,band:'severe'},{min:4,band:'critical'}],
            onset:'15–90 min (fast)', treat:'Decontaminate early; control excitation/seizures, IV fluids, cardiac & respiratory support. Vape refill liquid is highly concentrated and dangerous.',
            lethal:'~4 mg/kg', note:'Reference: cigarette ~9–30 mg total (butt ~5–7 mg); nicotine gum 2–4 mg; patch 8–114 mg; vape e-liquid up to 20+ mg/mL.'},
          cat:{bands:[{min:0.5,band:'severe'},{min:4,band:'critical'}], onset:'15–90 min', treat:'As per dog.', note:'Sensitive.'},
        },
        ownerNote:'Vape refill bottles and nicotine pouches are the modern hazard: far more concentrated than a cigarette. Patches and gum are dangerous too, even used ones.'
      },
      
      /* ---------------- PLANTS (companion) ---------------- */
      {
        id:'lily', name:'Lily (true / day lily): cats', cat:'Plants',
        aliases:['lily','lilium','hemerocallis','easter lily','tiger lily','stargazer','daylily'],
        species:{
          cat:{qualitative:true, band:'critical', msg:'For cats, ALL parts of true lilies (Lilium) and day lilies (Hemerocallis) are nephrotoxic: petals, leaves, pollen, even the vase water. A tiny amount can cause fatal kidney failure.',
            onset:'Vomiting 0–3 h; kidney failure 12–72 h', treat:'Decontaminate immediately; aggressive IV fluid diuresis for 48–72 h; monitor renal values. Treatment within 18 h gives the best chance.'},
          dog:{qualitative:true, band:'mild', msg:'True lilies cause only mild GI upset in dogs (not the kidney failure seen in cats). Lily of the valley is different: see that entry.'},
        },
        ownerNote:'This is the number-one "I didn\'t know" cat poisoning. If you own a cat, keep lilies out of the house entirely: even pollen brushed onto the coat and groomed off can be enough.'
      },
      {
        id:'lilyvalley', name:'Lily of the valley', cat:'Plants',
        aliases:['lily of the valley','convallaria','cardiac glycoside'],
        species:{
          dog:{qualitative:true, band:'severe', msg:'Contains cardiac glycosides: affects heart rhythm, unlike true lilies. Different plant, different danger.',
            onset:'0–12 h', treat:'Decontaminate; ECG monitoring, treat arrhythmias & hyperkalaemia; atropine for bradycardia; supportive.'},
          cat:{qualitative:true, band:'severe', msg:'As per dog: cardiac, not renal.'},
        },
        ownerNote:'Despite the name, this affects the heart, not the kidneys. Any known ingestion warrants an ECG.'
      },
      {
        id:'sago', name:'Sago palm / cycad', cat:'Plants',
        aliases:['sago palm','cycad','cycas','zamia','coontie'],
        species:{
          dog:{qualitative:true, band:'critical', msg:'All parts are toxic, seeds worst. Causes severe acute liver failure. High fatality rate even with treatment.',
            onset:'GI 0–24 h; liver failure 2–3 days', treat:'Aggressive decontamination; intensive hepatic support, plasma/clotting factors, anti-emetics; guarded prognosis.'},
          cat:{qualitative:true, band:'critical', msg:'As per dog.'},
        },
        ownerNote:'A popular ornamental palm that is deadly serious: a couple of seeds can cause fatal liver failure. If you have dogs, this plant is not worth the risk in the garden.'
      },
      {
        id:'oleander', name:'Oleander / foxglove / cardiac-glycoside plants', cat:'Plants',
        aliases:['oleander','foxglove','digitalis','nerium','cardiac glycoside','kalanchoe','milkweed'],
        species:{
          dog:{qualitative:true, band:'critical', msg:'Cardiac glycosides: a small amount of leaf can cause fatal arrhythmia.',
            onset:'0–12 h', treat:'Decontaminate; ECG, treat arrhythmias & hyperkalaemia; digoxin-specific Fab fragments if available; supportive.'},
          cat:{qualitative:true, band:'critical', msg:'As per dog.'},
          horse:{qualitative:true, band:'critical', msg:'Horses very sensitive; a few leaves (even in hay clippings) can be lethal.'},
          cattle:{qualitative:true, band:'critical', msg:'Highly toxic; often found dead. Common in dumped garden clippings.'},
          sheep:{qualitative:true, band:'critical', msg:'As per cattle.'},
          goat:{qualitative:true, band:'critical', msg:'As per cattle.'},
          pig:{qualitative:true, band:'critical', msg:'Access to clippings; cardiac glycoside toxicity.'},
        },
        ownerNote:'Oleander is one of the most toxic garden plants there is, and dried clippings in hay are just as deadly. Never dump garden waste where livestock or horses can reach it.'
      },
      
      /* ---------------- FARM & LIVESTOCK ---------------- */
      {
        id:'yew', name:'Yew (Taxus)', cat:'Farm & livestock plants',
        aliases:['yew','taxus','japanese yew','taxine'],
        species:{
          horse:{qualitative:true, band:'critical', msg:'One of the most acutely lethal plants known: as little as 0.1–0.5% of body weight in leaves can kill. Animals are often found dead near hedge clippings.',
            onset:'Minutes – hours', treat:'Frequently fatal before treatment. Decontamination, intensive cardiac support; guarded to grave prognosis.'},
          cattle:{qualitative:true, band:'critical', msg:'As per horse: sudden death is common.'},
          sheep:{qualitative:true, band:'critical', msg:'As per horse.'},
          goat:{qualitative:true, band:'critical', msg:'Slightly more resistant but still highly dangerous.'},
          dog:{qualitative:true, band:'severe', msg:'Less commonly eaten but toxic; cardiac and GI effects.'},
        },
        ownerNote:'Yew clippings thrown over a fence are a classic cause of sudden livestock death. If you keep stock, treat this plant as lethal and keep all hedge trimmings well away.'
      },
      {
        id:'ionophore', name:'Ionophores (monensin / lasalocid): feed', cat:'Farm & livestock plants',
        aliases:['monensin','ionophore','lasalocid','rumensin','poultry feed','coccidiostat'],
        species:{
          horse:{qualitative:true, band:'critical', msg:'Horses are extremely sensitive: accidental access to cattle/poultry feed containing monensin causes fatal heart-muscle damage. LD50 ~2–3 mg/kg.',
            onset:'Hours – days', treat:'No antidote. Decontaminate; strict rest, cardiac support; survivors may have permanent heart damage. Prognosis guarded.'},
          cattle:{qualitative:true, band:'severe', msg:'Toxic on overdose/mixing errors (LD50 ~20–80 mg/kg); cardiac and skeletal muscle damage.'},
          sheep:{qualitative:true, band:'severe', msg:'Sensitive; mixing errors dangerous.'},
          dog:{qualitative:true, band:'moderate', msg:'Can cause neuromuscular signs if eats medicated feed.'},
        },
        ownerNote:'Never let horses near cattle or poultry feed. Monensin is safe and useful in the target species but a small amount of the wrong feed can permanently damage a horse\'s heart.'
      },
      {
        id:'urea', name:'Urea / non-protein nitrogen (ruminants)', cat:'Farm & livestock plants',
        aliases:['urea','npn','non-protein nitrogen','ammonia','lick block'],
        doseUnit:'g/kg', active:'urea',
        inputs:[ num('g','Urea ingested (grams)','e.g. 200') ],
        dose:(v,kg)=>({value:v.g/kg, unit:'g/kg urea'}),
        species:{
          cattle:{bands:[{min:0.3,band:'severe'},{min:0.5,band:'critical'}],
            onset:'20 min – 4 h', treat:'Cold acidic drench (vinegar + cold water) to slow ammonia release; supportive. Unadapted animals at highest risk.',
            lethal:'~1–1.5 g/kg unadapted', note:'Ammonia toxicity from overfeeding urea/NPN supplements, especially unadapted stock or mixing errors.'},
          sheep:{bands:[{min:0.3,band:'severe'}], onset:'20 min – 4 h', treat:'As per cattle.', note:'As per cattle.'},
          goat:{bands:[{min:0.3,band:'severe'}], onset:'20 min – 4 h', treat:'As per cattle.', note:'As per cattle.'},
        },
        ownerNote:'Urea toxicity is usually a management error: a broken lick block, unadapted cattle, or a bad feed mix. The vinegar-and-cold-water drench buys time while you get veterinary help.'
      },
      {
        id:'nitrate', name:'Nitrate / nitrite (forage, fertiliser)', cat:'Farm & livestock plants',
        aliases:['nitrate','nitrite','fertiliser','pigweed','sorghum','oaten hay','drought forage'],
        species:{
          cattle:{qualitative:true, band:'severe', msg:'Nitrate-accumulating forages (especially after drought stress or heavy fertilising) convert to nitrite in the rumen, causing methaemoglobinaemia: chocolate-brown blood, gasping, sudden death.',
            onset:'30 min – hours', treat:'Methylene blue IV antidote; minimise handling/stress; remove source. Test suspect forage before feeding.'},
          sheep:{qualitative:true, band:'severe', msg:'As per cattle.'},
          horse:{qualitative:true, band:'moderate', msg:'Less sensitive than ruminants but affected at high intakes.'},
        },
        ownerNote:'Drought-stressed and heavily fertilised crops can stockpile nitrate. If you are feeding out unusual forage, get it tested: brown gums and sudden collapse in cattle is a nitrate red flag.'
      },
      {
        id:'cyanide', name:'Cyanogenic plants (prunus, sorghum)', cat:'Farm & livestock plants',
        aliases:['cyanide','prussic acid','sorghum','johnson grass','cherry','stone fruit','wilted leaves','hydrogen cyanide'],
        species:{
          cattle:{qualitative:true, band:'critical', msg:'Wilted stone-fruit (cherry, plum) leaves and stressed sorghum/johnson grass release cyanide. Death can occur within minutes of grazing.',
            onset:'Minutes', treat:'Sodium thiosulfate ± sodium nitrite IV antidote: must be immediate. Often found dead.'},
          sheep:{qualitative:true, band:'critical', msg:'As per cattle.'},
          goat:{qualitative:true, band:'critical', msg:'As per cattle.'},
          horse:{qualitative:true, band:'severe', msg:'Less commonly acute cyanide; sorghum also causes cystitis/ataxia syndrome in horses.'},
        },
        ownerNote:'Wilted or frost-damaged cherry/plum leaves and stressed sorghum are the danger: the cyanide is highest when the plant is stressed. Fence stock away from stone-fruit trees, especially after a storm drops branches.'
      },
      {
        id:'lantana', name:'Lantana', cat:'Farm & livestock plants',
        aliases:['lantana','lantadene','photosensitisation'],
        species:{
          cattle:{qualitative:true, band:'severe', msg:'Lantadenes cause liver damage and secondary photosensitisation (sunburn on pink/white skin), plus GI stasis. A major weed poisoning in warm climates.',
            onset:'1–2 days', treat:'Remove from lantana; shade; activated charcoal early; supportive liver care; prolonged recovery.'},
          sheep:{qualitative:true, band:'severe', msg:'As per cattle.'},
          goat:{qualitative:true, band:'moderate', msg:'Somewhat more resistant but affected.'},
        },
        ownerNote:'Hungry or newly-introduced stock are most at risk of eating lantana. Sunburnt, peeling skin on the muzzle and back plus yellow gums points to lantana liver damage.'
      },
      {
        id:'bracken', name:'Bracken fern', cat:'Farm & livestock plants',
        aliases:['bracken','pteridium','thiamine','ptaquiloside'],
        species:{
          cattle:{qualitative:true, band:'severe', msg:'Chronic ingestion causes bone-marrow suppression, bleeding and bladder tumours; a cumulative poison over weeks.',
            onset:'Weeks', treat:'Remove from bracken; supportive care, transfusion if bleeding; guarded once marrow affected.'},
          horse:{qualitative:true, band:'moderate', msg:'Causes thiamine (B1) deficiency: staggers; responds to thiamine injections.'},
          sheep:{qualitative:true, band:'moderate', msg:'Bright blindness (progressive retinal degeneration) with chronic intake.'},
        },
        ownerNote:'Bracken is a slow poison: the damage builds over weeks of grazing, so it is a pasture-management problem rather than a single-meal emergency.'
      },
      {
        id:'avocado', name:'Avocado (persin)', cat:'Farm & livestock plants',
        aliases:['avocado','persin'],
        species:{
          bird:{qualitative:true, band:'severe', msg:'Birds are highly sensitive: avocado can cause acute heart failure and sudden death. Never feed to birds.',
            onset:'Hours', treat:'Supportive; often fatal in birds. Prevention is key.'},
          horse:{qualitative:true, band:'moderate', msg:'Oral/facial and mammary oedema, colic; keep horses away from avocado trees.'},
          cattle:{qualitative:true, band:'moderate', msg:'Mastitis and cardiac effects.'},
          dog:{qualitative:true, band:'mild', msg:'Mostly mild GI upset in dogs; the bigger risk is the large pit causing an obstruction.'},
          cat:{qualitative:true, band:'mild', msg:'Mild GI upset; pit is a choking/obstruction risk.'},
        },
        ownerNote:'For dogs and cats avocado flesh is a minor worry: the real hazard is the slippery pit swallowed whole. For birds, though, avocado is genuinely deadly.'
      },
      {
        id:'bluegreenalgae', name:'Blue-green algae (cyanobacteria)', cat:'Farm & livestock plants',
        aliases:['blue-green algae','cyanobacteria','microcystin','anatoxin','pond scum','algal bloom'],
        species:{
          dog:{qualitative:true, band:'critical', msg:'Toxic blooms in warm, still water produce liver toxins (microcystin) or fast-acting nerve toxins (anatoxin-a). Dogs that swim in or drink from scummy water can die within minutes to hours.',
            onset:'Minutes (neurotoxin) – hours (hepatotoxin)', treat:'No specific antidote; aggressive decontamination if very early, intensive supportive/hepatic care. Prognosis often poor.'},
          cat:{qualitative:true, band:'critical', msg:'As per dog.'},
          cattle:{qualitative:true, band:'critical', msg:'Livestock drinking from affected dams/ponds are found dead near the water.'},
          sheep:{qualitative:true, band:'critical', msg:'As per cattle.'},
          horse:{qualitative:true, band:'critical', msg:'As per cattle.'},
          goat:{qualitative:true, band:'critical', msg:'As per cattle.'},
          pig:{qualitative:true, band:'critical', msg:'As per cattle: access to affected water sources.'},
        },
        ownerNote:'If a pond, lake or dam looks like green paint or pea soup in hot weather, keep every animal out of it. This kills fast, and there is no antidote: prevention is the whole game.'
      },
      ];
      
      /* ================= COMMON PORTIONS =================
         Quick-fill presets so owners don't have to guess grams/mg.
         Each sets one or more input field ids. Keyed by toxin id. */
      const PORTIONS = {
        chocolate:[
          {label:'Few squares milk (~20g)', v:{g:20,type:2.0}},
          {label:'Small milk bar (~45g)', v:{g:45,type:2.0}},
          {label:'Large milk block (~180g)', v:{g:180,type:2.0}},
          {label:'Dark 70% block (~100g)', v:{g:100,type:8}},
          {label:'Handful choc chips (~50g)', v:{g:50,type:5.5}},
          {label:'Baking block (~250g)', v:{g:250,type:16}},
          {label:'1 tbsp cocoa powder (~5g)', v:{g:5,type:20}},
        ],
        xylitol:[
          {label:'1 piece gum (~0.3g pure)', v:{g:0.3,type:1000}},
          {label:'5 pieces gum (~1.5g pure)', v:{g:1.5,type:1000}},
          {label:'Whole pack gum (~5g pure)', v:{g:5,type:1000}},
          {label:'1 tsp xylitol powder (~4g)', v:{g:4,type:1000}},
          {label:'1 tbsp xylitol peanut butter (~15g)', v:{g:15,type:30}},
          {label:'1 xylitol mint (~0.5g pure)', v:{g:0.5,type:1000}},
        ],
        allium:[
          {label:'1 small raw onion (~100g)', v:{g:100,type:1}},
          {label:'1 large onion (~200g)', v:{g:200,type:1}},
          {label:'1 garlic clove (~5g)', v:{g:5,type:5}},
          {label:'1 tsp garlic paste (~5g)', v:{g:5,type:5}},
          {label:'1 tsp onion powder (~2.5g)', v:{g:2.5,type:8}},
          {label:'1 tsp garlic powder (~3g)', v:{g:3,type:40}},
        ],
        macadamia:[
          {label:'5 nuts (~10g)', v:{g:10}},
          {label:'10 nuts (~20g)', v:{g:20}},
          {label:'Small handful (~30g)', v:{g:30}},
          {label:'Half a packet (~50g)', v:{g:50}},
        ],
        caffeine:[
          {label:'1 cup brewed coffee (~95mg)', v:{mg:95}},
          {label:'1 espresso shot (~65mg)', v:{mg:65}},
          {label:'1 cup tea (~45mg)', v:{mg:45}},
          {label:'1 energy drink (~120mg)', v:{mg:120}},
          {label:'1 caffeine/No-Doz pill (~200mg)', v:{mg:200}},
          {label:'1 scoop pre-workout (~250mg)', v:{mg:250}},
        ],
        ethanol:[
          {label:'1 can beer (375ml)', v:{ml:375,abv:0.05}},
          {label:'1 glass wine (150ml)', v:{ml:150,abv:0.12}},
          {label:'1 shot spirits (30ml)', v:{ml:30,abv:0.40}},
          {label:'1 standard nip (45ml)', v:{ml:45,abv:0.40}},
        ],
        salt:[
          {label:'1 tsp salt (~6g)', v:{g:6}},
          {label:'1 tbsp salt (~18g)', v:{g:18}},
          {label:'1 salt-dough ornament (~30g)', v:{g:30}},
          {label:'Small paintball handful (~15g)', v:{g:15}},
        ],
        paracetamol:[
          {label:'1 standard tablet (500mg)', v:{n:1,str:500}},
          {label:'2 tablets (500mg)', v:{n:2,str:500}},
          {label:'1 extra-strength (665mg)', v:{n:1,str:665}},
          {label:'1 x 1000mg', v:{n:1,str:1000}},
        ],
        ibuprofen:[
          {label:'1 tablet (200mg)', v:{n:1,str:200}},
          {label:'2 tablets (200mg)', v:{n:2,str:200}},
          {label:'1 x 400mg', v:{n:1,str:400}},
          {label:'4 tablets (200mg)', v:{n:4,str:200}},
        ],
        naproxen:[
          {label:'1 tablet (220mg)', v:{n:1,str:220}},
          {label:'1 x 250mg', v:{n:1,str:250}},
          {label:'1 x 500mg', v:{n:1,str:500}},
        ],
        aspirin:[
          {label:'1 low-dose (100mg)', v:{n:1,str:100}},
          {label:'1 standard (300mg)', v:{n:1,str:300}},
          {label:'2 x 300mg', v:{n:2,str:300}},
        ],
        pseudoephedrine:[
          {label:'1 x 30mg', v:{n:1,str:30}},
          {label:'1 x 60mg', v:{n:1,str:60}},
          {label:'1 x 120mg (slow-release)', v:{n:1,str:120}},
          {label:'1 x 240mg (slow-release)', v:{n:1,str:240}},
        ],
        adhd:[
          {label:'1 x 10mg', v:{mg:10}},
          {label:'1 x 20mg', v:{mg:20}},
          {label:'1 x 30mg (XR)', v:{mg:30}},
          {label:'1 x 50mg (Vyvanse)', v:{mg:50}},
        ],
        baclofen:[
          {label:'1 x 10mg', v:{mg:10}},
          {label:'2 x 10mg', v:{mg:20}},
          {label:'1 x 25mg', v:{mg:25}},
        ],
        iron:[
          {label:'1 adult multivitamin (~18mg elem)', v:{mg:18}},
          {label:'1 prenatal (~27mg elem)', v:{mg:27}},
          {label:'1 ferrous sulfate 325mg (~65mg elem)', v:{mg:65}},
          {label:'5 gummy vitamins (~10mg elem)', v:{mg:10}},
        ],
        vitd:[
          {label:'1 x 1000 IU', v:{iu:1000}},
          {label:'1 x 5000 IU', v:{iu:5000}},
          {label:'1 monthly script (50,000 IU)', v:{iu:50000}},
        ],
        anticoag:[
          {label:'Small mouthful pellets (~5g)', v:{g:5,type:0.05}},
          {label:'Half a bait block (~10g)', v:{g:10,type:0.05}},
          {label:'Whole bait block (~20g)', v:{g:20,type:0.05}},
        ],
        bromethalin:[
          {label:'Small mouthful (~5g)', v:{g:5,type:0.1}},
          {label:'Half a block (~10g)', v:{g:10,type:0.1}},
          {label:'Whole block (~20g)', v:{g:20,type:0.1}},
        ],
        cholecalciferol:[
          {label:'Small mouthful (~5g)', v:{g:5,type:0.75}},
          {label:'Half a block (~10g)', v:{g:10,type:0.75}},
          {label:'Whole block (~20g)', v:{g:20,type:0.75}},
        ],
        metaldehyde:[
          {label:'1 tsp pellets (~5g)', v:{g:5,type:30}},
          {label:'1 tbsp pellets (~15g)', v:{g:15,type:30}},
          {label:'Small handful (~25g)', v:{g:25,type:30}},
        ],
        ethyleneglycol:[
          {label:'A few licks (~5ml)', v:{ml:5,conc:0.95}},
          {label:'1 tbsp (~15ml)', v:{ml:15,conc:0.95}},
          {label:'Small puddle, pre-mix (~30ml)', v:{ml:30,conc:0.50}},
        ],
        thc:[
          {label:'Small scrap / 1 puff (~2mg)', v:{mg:2}},
          {label:'1 gummy/edible (~10mg)', v:{mg:10}},
          {label:'Strong edible (~50mg)', v:{mg:50}},
          {label:'Cannabis butter / concentrate (~100mg)', v:{mg:100}},
        ],
        nicotine:[
          {label:'1 cigarette butt (~5mg)', v:{mg:5}},
          {label:'1 whole cigarette (~16mg)', v:{mg:16}},
          {label:'1 nicotine gum (~4mg)', v:{mg:4}},
          {label:'1 pouch/snus (~10mg)', v:{mg:10}},
          {label:'1 ml vape refill (~20mg)', v:{mg:20}},
          {label:'1 patch (~50mg)', v:{mg:50}},
        ],
        urea:[
          {label:'Mouthful of lick block (~50g)', v:{g:50}},
          {label:'~200g supplement', v:{g:200}},
          {label:'~500g (major spill)', v:{g:500}},
        ],
      };
      
      /* ================= STATE ================= */
      let state = { mode:'owner', species:null, weightUnit:'kg', weight:null, toxin:null, inputs:{}, timeSince:null, certainty:'exact', symptomatic:false, route:'oral', productName:'', showVet:false };
      
      const ROUTES = [
        {id:'oral',label:'Swallowed'},
        {id:'skin',label:'Skin / coat'},
        {id:'eye',label:'Eye'},
        {id:'inhaled',label:'Inhaled'},
        {id:'unknown',label:'Unknown'},
      ];
      
      function resetExposureState(){
        state.inputs={}; state.timeSince=null; state.certainty='exact'; state.symptomatic=false;
        state.route='oral'; state.productName=''; state.showVet=false;
      }
      
      function inputsComplete(t){
        if(!t || !state.species)return false;
        const sp=t.species[state.species];
        if(sp?.qualitative || state.route!=='oral')return true;
        return (t.inputs||[]).filter(f=>f.type!=='select').every(f=>Number.isFinite(state.inputs[f.id])&&state.inputs[f.id]>0);
      }
      
      function updateProgress(){
        const t=currentToxin();
        const sp=t&&state.species?t.species[state.species]:null;
        const weightOptional=Boolean(t&&(sp?.qualitative||state.route!=='oral'));
        const complete=[Boolean(state.species),Boolean(weightKg())||weightOptional,Boolean(t),Boolean(t&&inputsComplete(t))];
        let current=complete.findIndex(v=>!v); if(current<0)current=3;
        document.querySelectorAll('.progress-step').forEach((step,i)=>{
          step.classList.toggle('done',complete[i] && i<current);
          step.classList.toggle('current',i===current);
          step.setAttribute('aria-current',i===current?'step':'false');
          step.querySelector('.step-num').textContent=complete[i]&&i<current?'✓':String(i+1);
        });
      }
      
      document.getElementById('progressNav').addEventListener('click',e=>{
        const step=e.target.closest('.progress-step'); if(!step)return;
        document.getElementById(step.dataset.target)?.scrollIntoView({behavior:'smooth',block:'start'});
      });
      
      /* ================= RENDER: species ================= */
      const speciesGrid = document.getElementById('speciesGrid');
      SPECIES.forEach(s=>{
        const b=document.createElement('button');
        b.type='button';
        b.setAttribute('aria-label',`Select ${s.name}`);
        b.innerHTML=`<span class="ico">${s.ico}</span>${s.name}`;
        b.onclick=()=>{ state.species=s.id; state.toxin=null; resetExposureState();
          [...speciesGrid.children].forEach(c=>c.classList.remove('active'));
          b.classList.add('active'); renderToxList(); renderExposure(); renderResult(); };
        speciesGrid.appendChild(b);
      });
      
      /* ================= mode toggle ================= */
      document.getElementById('modewrap').addEventListener('click',e=>{
        const btn=e.target.closest('button'); if(!btn)return;
        state.mode=btn.dataset.mode;
        [...e.currentTarget.children].forEach(c=>c.classList.remove('active'));
        btn.classList.add('active'); renderResult();
      });
      
      /* ================= weight ================= */
      const weightInput=document.getElementById('weight');
      weightInput.addEventListener('input',()=>{ state.weight=parseFloat(weightInput.value)||null; renderResult(); updateProgress(); });
      document.getElementById('weightUnit').addEventListener('click',e=>{
        const btn=e.target.closest('button'); if(!btn)return;
        const nextUnit=btn.dataset.u;
        if(nextUnit===state.weightUnit)return;
        if(Number.isFinite(state.weight) && state.weight>0){
          state.weight = nextUnit==='lb' ? state.weight*2.2046226218 : state.weight/2.2046226218;
          weightInput.value = Number(state.weight.toFixed(2));
        }
        state.weightUnit=nextUnit;
        [...e.currentTarget.children].forEach(c=>c.classList.remove('active'));
        btn.classList.add('active');
        document.getElementById('weightHint').textContent = state.weightUnit==='kg'
          ? "Enter the animal's current weight." : "Weight in pounds: converted to kg automatically.";
        renderResult(); updateProgress();
      });
      function weightKg(){ if(!state.weight)return null; return state.weightUnit==='lb'? state.weight*0.453592 : state.weight; }
      
      /* ================= toxin list ================= */
      const searchBox=document.getElementById('search');
      searchBox.addEventListener('input',renderToxList);
      document.getElementById('clearSearch').addEventListener('click',()=>{ searchBox.value=''; searchBox.focus(); renderToxList(); });
      function toxAppliesToSpecies(t){ return state.species && t.species[state.species]; }
      
      function renderToxList(){
        const list=document.getElementById('toxList'); const meta=document.getElementById('toxMeta'); list.innerHTML='';
        if(!state.species){ meta.textContent='Choose a species to see applicable toxins.'; list.innerHTML='<div class="muted-block" style="padding:14px">Select a species first.</div>'; updateProgress(); return; }
        const q=(searchBox.value||'').toLowerCase().trim();
        const filtered=TOXINS.filter(t=>{
          if(!toxAppliesToSpecies(t))return false;
          if(!q)return true;
          return t.name.toLowerCase().includes(q) || (t.aliases||[]).some(a=>String(a).toLowerCase().includes(q)) || t.cat.toLowerCase().includes(q);
        });
        const spName=SPECIES.find(s=>s.id===state.species).name;
        meta.textContent=`${filtered.length} ${filtered.length===1?'toxin':'toxins'} shown for ${spName.toLowerCase()}${q?' · filtered search':''}.`;
        if(!filtered.length){ list.innerHTML='<div class="muted-block" style="padding:14px">No matching toxin found. Check the spelling or contact a vet if the substance is not listed.</div>'; updateProgress(); return; }
        const cats=[...new Set(filtered.map(t=>t.cat))];
        cats.forEach(cat=>{
          const h=document.createElement('div'); h.className='tox-cat'; h.textContent=cat; list.appendChild(h);
          filtered.filter(t=>t.cat===cat).forEach(t=>{
            const sp=t.species[state.species];
            const item=document.createElement('div'); item.className='tox-item'+(state.toxin===t.id?' active':'');
            let tag=''; if(sp.qualitative) tag='<span class="q">ANY AMOUNT</span>';
            item.innerHTML=`<span>${t.name}</span>${tag}`;
            item.setAttribute('role','button'); item.tabIndex=0;
            item.onclick=()=>{ state.toxin=t.id; resetExposureState(); renderToxList(); renderExposure(); renderResult(); updateProgress(); };
            item.onkeydown=e=>{ if(e.key==='Enter'||e.key===' '){e.preventDefault();item.click();} };
            list.appendChild(item);
          });
        });
        updateProgress();
      }
      
      /* ================= exposure inputs ================= */
      function currentToxin(){ return TOXINS.find(t=>t.id===state.toxin); }
      const TIME_OPTS=[
        {id:'unknown',label:'Unknown'},{id:'lt1',label:'Under 1 h'},{id:'1to2',label:'1–2 h'},
        {id:'2to4',label:'2–4 h'},{id:'4to12',label:'4–12 h'},{id:'gt12',label:'Over 12 h'},
      ];
      
      function renderTimeField(box){
        const wrap=document.createElement('div');
        wrap.innerHTML='<label>Time since exposure (helps with treatment window)</label>';
        const grid=document.createElement('div'); grid.className='timegrid';
        TIME_OPTS.forEach(o=>{
          const b=document.createElement('button'); b.type='button'; b.textContent=o.label;
          if(state.timeSince===o.id)b.classList.add('active');
          b.onclick=()=>{ state.timeSince=o.id; renderExposure(); renderResult(); };
          grid.appendChild(b);
        });
        wrap.appendChild(grid); box.appendChild(wrap);
      }
      
      function renderSafetyFields(box){
        const certainty=document.createElement('div');
        certainty.innerHTML='<label>How certain is the amount?</label>';
        const cg=document.createElement('div'); cg.className='uncertainty';
        [['exact','Measured'],['estimated','Estimated'],['unknown','Unknown']].forEach(([id,label])=>{
          const b=document.createElement('button'); b.type='button'; b.textContent=label;
          if(state.certainty===id)b.classList.add('active');
          b.onclick=()=>{state.certainty=id;renderExposure();renderResult();}; cg.appendChild(b);
        });
        certainty.appendChild(cg); box.appendChild(certainty);
        const check=document.createElement('div'); check.className='safety-check';
        const b=document.createElement('button'); b.type='button';
        b.textContent='Animal has symptoms now'; b.setAttribute('aria-pressed',String(state.symptomatic));
        if(state.symptomatic)b.classList.add('active');
        b.onclick=()=>{state.symptomatic=!state.symptomatic;renderExposure();renderResult();};
        check.appendChild(b); box.appendChild(check);
      }
      
      function renderRouteFields(box){
        const wrap=document.createElement('div');
        const label=document.createElement('label'); label.textContent='How was the animal exposed?'; wrap.appendChild(label);
        const grid=document.createElement('div'); grid.className='route-grid';
        ROUTES.forEach(route=>{
          const b=document.createElement('button'); b.type='button'; b.textContent=route.label;
          if(state.route===route.id)b.classList.add('active');
          b.onclick=()=>{state.route=route.id;renderExposure();renderResult();updateProgress();}; grid.appendChild(b);
        });
        wrap.appendChild(grid);
        if(state.route!=='oral'){
          const notes={
            skin:'Oral dose thresholds do not reliably assess skin or coat exposure. Prevent grooming and contact a veterinarian or poison service before applying a product or home remedy.',
            eye:'Begin flushing the eye gently with lukewarm water or saline for 15 minutes, if it is safe to do so, then contact a veterinarian.',
            inhaled:'Move the animal into fresh air only if you can do so without exposing yourself. Breathing difficulty is an immediate emergency.',
            unknown:'The exposure route is unknown, so the calculator cannot safely estimate absorption. Contact a veterinarian or poison service.',
          };
          const note=document.createElement('div'); note.className='route-note'; note.textContent=notes[state.route]; wrap.appendChild(note);
        }
        const productLabel=document.createElement('label'); productLabel.textContent='Product or brand name (optional)'; wrap.appendChild(productLabel);
        const product=document.createElement('input'); product.type='text'; product.placeholder='e.g. Panadol, bait brand, plant name'; product.value=state.productName;
        product.oninput=()=>{state.productName=product.value.trim();}; wrap.appendChild(product);
        box.appendChild(wrap);
      }
      
      function renderExposure(){
        const box=document.getElementById('exposure');
        const t=currentToxin();
        if(!t){ box.innerHTML='<div class="muted-block">Pick a species and a toxin to begin.</div>'; return; }
        const sp=t.species[state.species];
        if(sp.qualitative || !t.inputs){
          box.innerHTML='';
          renderRouteFields(box);
          const msg=document.createElement('div'); msg.className='muted-block';
          msg.textContent='This exposure is assessed qualitatively for this species because a reliable mg/kg threshold is not available. Record the route and timing, then follow the guidance on the right.';
          box.appendChild(msg);
          renderTimeField(box);
          renderSafetyFields(box);
          updateProgress();
          return;
        }
        box.innerHTML='';
        renderRouteFields(box);
      
        if(state.route!=='oral'){
          renderTimeField(box);
          renderSafetyFields(box);
          updateProgress();
          return;
        }
      
        // Quick-fill chips
        const portions=PORTIONS[t.id];
        if(portions){
          const qf=document.createElement('div'); qf.className='qf';
          qf.innerHTML='<div class="qftitle">Quick fill: tap a common amount</div>';
          const chips=document.createElement('div'); chips.className='chips';
          portions.forEach(p=>{
            const c=document.createElement('button'); c.type='button'; c.className='chip'; c.textContent=p.label;
            const isActive=Object.entries(p.v).every(([k,val])=>state.inputs[k]===val);
            if(isActive)c.classList.add('active');
            c.onclick=()=>{ Object.assign(state.inputs,p.v); renderExposure(); renderResult(); };
            chips.appendChild(c);
          });
          qf.appendChild(chips); box.appendChild(qf);
          const or=document.createElement('div'); or.className='hint'; or.style.margin='8px 0 2px';
          or.textContent='…or enter the amount manually below.'; box.appendChild(or);
        }
      
        t.inputs.forEach(f=>{
          const lab=document.createElement('label'); lab.textContent=f.label; box.appendChild(lab);
          if(f.type==='select'){
            const sel=document.createElement('select');
            if(state.inputs[f.id]===undefined) state.inputs[f.id]=parseFloat(f.options[0].value);
            f.options.forEach(o=>{ const op=document.createElement('option'); op.value=o.value; op.textContent=o.label;
              if(parseFloat(o.value)===state.inputs[f.id])op.selected=true; sel.appendChild(op); });
            sel.onchange=()=>{ state.inputs[f.id]=parseFloat(sel.value); renderExposure(); renderResult(); };
            box.appendChild(sel);
          } else {
            const inp=document.createElement('input'); inp.type='number'; inp.min='0'; inp.step='any'; inp.placeholder=f.placeholder||'';
            if(state.inputs[f.id]!=null) inp.value=state.inputs[f.id];
            inp.oninput=()=>{ state.inputs[f.id]=parseFloat(inp.value)||null; renderResultOnly(); };
            box.appendChild(inp);
          }
          if(f.hint){ const h=document.createElement('div'); h.className='hint'; h.textContent=f.hint; box.appendChild(h); }
        });
      
        renderTimeField(box);
        renderSafetyFields(box);
        updateProgress();
      }
      /* update result without rebuilding the exposure fields (keeps focus while typing) */
      function renderResultOnly(){ renderResult(); }
      
      /* ================= band resolution ================= */
      function resolveBand(bands,dose){
        let cur='safe';
        for(const b of bands){ if(dose>=b.min) cur=b.band; }
        return cur;
      }
      function bandClass(b){ return 'b-'+b; }
      
      function routeGuidance(route){
        const guidance={
          skin:{title:'Skin or coat exposure',body:'The oral mg/kg calculation does not apply to dermal exposure. Prevent grooming, remove the animal from the source and contact a veterinarian or poison service for product-specific decontamination advice.'},
          eye:{title:'Eye exposure',body:'Gently flush with lukewarm water or saline for 15 minutes if safe, then contact a veterinarian. Do not put medication or neutralising chemicals into the eye unless directed.'},
          inhaled:{title:'Inhalation exposure',body:'Move the animal into fresh air only if it is safe for you. Coughing, weakness, abnormal gum colour or breathing difficulty requires immediate emergency assessment.'},
          unknown:{title:'Exposure route unknown',body:'Absorption cannot be estimated safely without knowing how exposure occurred. Contact a veterinarian or animal poison service and bring the product packaging.'},
        };
        return guidance[route];
      }
      
      /* ================= result ================= */
      function renderResult(){
        const box=document.getElementById('result');
        const t=currentToxin();
        if(!t){
          const text=!state.species?'Choose the species to begin.':'Enter the weight, then choose the suspected toxin.';
          box.innerHTML=`<div class="pending-result"><div class="pending-icon">!</div><strong>Your risk result will appear here</strong><span>${text}</span></div>`;
          updateProgress(); return;
        }
        const sp=t.species[state.species];
        const spName=SPECIES.find(s=>s.id===state.species).name;
        const override = state.symptomatic || state.certainty==='unknown';
      
        // QUALITATIVE toxins
        if(sp.qualitative){
          const band=override && BAND_INDEX[sp.band||'severe']<3 ? 'severe' : (sp.band||'severe');
          box.innerHTML=`
            <div class="band ${bandClass(band)}">
              <div class="lvl">${BAND_LABEL[band]} · ${spName}</div>
              <div class="dose" style="font-size:22px">${t.name}</div>
              <div class="verdict">${override?(state.symptomatic?'Symptoms reported: seek veterinary advice now.':'Amount unknown: a dose calculation cannot rule out poisoning.') : sp.msg}</div>
              <div class="advice">${state.mode==='owner'?OWNER_ADVICE[band]:''}</div>
              ${deconNote(sp,band)}
              ${band==='critical'||band==='severe'?'<div class="callvet">Call your vet or emergency clinic now.</div>':''}
            </div>
            ${clinicalBlock(t,sp,null)}
            ${ownerNoteBlock(t)}
            ${actionsHtml(band)}
            ${vetPanelHtml()}
          `;
          wireActions(t,sp,band,null);
          updateProgress();
          return;
        }
      
        if(state.route!=='oral'){
          const route=routeGuidance(state.route);
          const band=(state.symptomatic||state.route==='unknown')?'severe':'moderate';
          box.innerHTML=`
            <div class="band ${bandClass(band)}">
              <div class="lvl">${BAND_LABEL[band]} · ${spName} · ${t.name}</div>
              <div class="dose" style="font-size:22px">${route.title}</div>
              <div class="verdict">An oral dose cannot be calculated for this exposure route.</div>
              <div class="advice">${route.body}</div>
              ${deconNote(sp,band)}
              <div class="callvet">Contact a veterinarian or animal poison service now.</div>
            </div>
            ${clinicalBlock(t,sp,null)}
            ${ownerNoteBlock(t)}
            ${actionsHtml(band)}
            ${vetPanelHtml()}`;
          wireActions(t,sp,band,null); updateProgress();
          return;
        }
      
        const kg=weightKg();
        if(!kg){ box.innerHTML=`<div class="pending-result"><div class="pending-icon">2</div><strong>Body weight needed</strong><span>Enter the animal's current weight to calculate the dose per kilogram.</span></div>`; updateProgress(); return; }
      
        // check inputs present
        const need=t.inputs.filter(f=>f.type!=='select');
        const missing=need.some(f=>!state.inputs[f.id]);
        if(missing && override){
          const band='severe';
          box.innerHTML=`
            <div class="band ${bandClass(band)}">
              <div class="lvl">Urgent assessment · ${spName} · ${t.name}</div>
              <div class="dose" style="font-size:22px">Dose cannot be calculated</div>
              <div class="verdict">${state.symptomatic?'Symptoms are present.':'The amount is unknown.'} Contact a veterinarian or animal poison service now.</div>
              <div class="advice">Do not wait for the calculator and do not induce vomiting unless specifically directed.</div>
              ${deconNote(sp,band)}
              <div class="callvet">Contact a vet or emergency clinic now.</div>
            </div>
            ${clinicalBlock(t,sp,null)}
            ${ownerNoteBlock(t)}
            ${actionsHtml(band)}
            ${vetPanelHtml()}`;
          wireActions(t,sp,band,null);
          updateProgress();
          return;
        }
        if(missing){ box.innerHTML=`<div class="pending-result"><div class="pending-icon">4</div><strong>Exposure amount needed</strong><span>Enter the amount swallowed to calculate the estimated dose.</span></div>`; updateProgress(); return; }
      
        const d=t.dose(state.inputs,kg);
        let band=resolveBand(sp.bands,d.value);
        if(state.certainty==='estimated' && band!=='critical') band=BANDS[Math.min(4,BAND_INDEX[band]+1)];
        if(override && BAND_INDEX[band]<3) band='severe';
        const idx=BAND_INDEX[band];
        const verdict = override
          ? (state.symptomatic?'Symptoms override the calculated dose: contact a veterinarian now.':'The amount is unknown, so this calculation cannot rule out a toxic exposure.')
          : state.mode==='owner'?OWNER_VERDICT[band]:`Calculated dose falls in the ${BAND_LABEL[band].toLowerCase()} range.`;
        const advice = state.mode==='owner'?OWNER_ADVICE[band]:'';
      
        box.innerHTML=`
          <div class="band ${bandClass(band)}">
            <div class="lvl">${BAND_LABEL[band]} · ${spName} · ${t.name}</div>
            <div class="dose">${fmt(d.value)} <span style="font-size:15px;font-weight:600">${d.unit}</span></div>
            <div class="doseSub">Based on ${fmt(state.weight)} ${state.weightUnit} (${fmt(kg)} kg)${state.certainty==='estimated'?' · estimated amount, risk band raised one level':''}</div>
            <div class="scale">
              <span class="s0"></span><span class="s1"></span><span class="s2"></span><span class="s3"></span><span class="s4"></span>
            </div>
            <div class="marker"><div class="dot" style="left:${idx*20+10}%"></div></div>
            <div class="scale-labels">
              <span>Below concern</span><span>Monitor</span><span>Moderate</span><span>Severe</span><span>Critical</span>
            </div>
            <div class="verdict">${verdict}</div>
            <div class="advice">${advice}</div>
            ${deconNote(sp,band)}
            ${(band==='critical'||band==='severe')?'<div class="callvet">This is an emergency: contact a vet now.</div>':''}
          </div>
          ${clinicalBlock(t,sp,d)}
          ${ownerNoteBlock(t)}
          ${actionsHtml(band)}
          ${vetPanelHtml()}
        `;
        wireActions(t,sp,band,d);
        updateProgress();
      }
      
      /* ---- decontamination / treatment-window note based on time since eaten ---- */
      function deconNote(sp,band){
        if(state.route!=='oral') return '';
        if(!state.timeSince || state.timeSince==='unknown') return '';
        if(band==='safe') return '';
        const t=state.timeSince;
        const label=TIME_OPTS.find(o=>o.id===t).label;
        if(t==='lt1'||t==='1to2'){
          return `<div class="decon open"><b>Timing (${label}):</b> This is within the window where a vet can often still decontaminate (induce vomiting / give activated charcoal). Do NOT try this at home: get to a vet quickly so they can decide.</div>`;
        }
        if(t==='2to4'){
          return `<div class="decon closing"><b>Timing (${label}):</b> Decontamination may still be appropriate for some substances or formulations. Call a vet now; do not try to make the animal vomit at home.</div>`;
        }
        return `<div class="decon late"><b>Timing (${label}):</b> Do not assume it is too late to help. Many toxins have delayed effects and still need antidotes, testing or monitoring. Call a vet or animal poison service for toxin-specific advice.</div>`;
      }
      
      /* ---- action buttons: print + vet chat ---- */
      function actionsHtml(band){
        const showVet = band==='moderate'||band==='severe'||band==='critical';
        return `<div class="actions">
          <button type="button" class="btn btn-print" id="btnPrint">Print / save PDF</button>
          <button type="button" class="btn btn-copy" id="btnCopy">Copy summary</button>
          <button type="button" class="btn btn-email" id="btnEmail">Email summary</button>
          ${showVet?`<button type="button" class="btn btn-vet" id="btnVet">Get veterinary help</button>`:''}
          <button type="button" class="btn btn-reset" id="btnReset">Start again</button>
          <div class="action-status" id="actionStatus" role="status" aria-live="polite"></div>
        </div>`;
      }
      function vetPanelHtml(){
        if(!state.showVet) return '';
        return `<div class="vetpanel">
          <span class="premium">ASK A VET™ PREMIUM</span>
          <h3>Talk to a certified vet now</h3>
          <p>A calculator can't examine your animal. A certified vet can look at the amount, timing and your pet's history and tell you exactly what to do next.</p>
          <ul>
            <li>Premium includes <b>2 certified vet chats every month</b>.</li>
            <li>A chat starts the moment you open it and stays open until the vet or you close it.</li>
            <li>Chats auto-close after 24 hours of inactivity.</li>
          </ul>
          <p style="color:var(--muted);font-size:12px">If this is a life-threatening emergency, go straight to your nearest open clinic: don't wait on a chat.</p>
          <div class="vetlinks">
            <a class="primary" href="${escapeHtml(GTC_CONFIG.vetChatUrl || 'https://get.askavet.com')}" target="_blank" rel="noopener">Start Vet Chat</a>
            <a class="secondary" href="${escapeHtml(GTC_CONFIG.emergencyVetUrl || 'https://www.google.com/maps/search/emergency+veterinarian+near+me')}" target="_blank" rel="noopener">Find Emergency Vet</a>
          </div>
          <button type="button" class="vetclose" id="btnVetClose">Close</button>
        </div>`;
      }
      function routeLabel(){ return (ROUTES.find(r=>r.id===state.route)||{}).label||'Not recorded'; }
      function plainAmount(t,d){
        if(!d)return state.route==='oral'?'Any exposure / amount not calculated':'Not applicable to this exposure route';
        const parts=[];
        (t.inputs||[]).forEach(f=>{
          if(state.inputs[f.id]!=null){
            let value=state.inputs[f.id];
            if(f.type==='select'){const option=f.options.find(o=>parseFloat(o.value)===value);value=option?option.label:value;}
            parts.push(`${f.label}: ${value}`);
          }
        });
        return `${parts.join(' | ')} | Estimated dose: ${fmt(d.value)} ${d.unit}`;
      }
      function summaryText(t,band,d){
        const spName=SPECIES.find(s=>s.id===state.species)?.name||'Not recorded';
        const kg=weightKg();
        const timeLabel=state.timeSince?(TIME_OPTS.find(o=>o.id===state.timeSince)||{}).label:'Not recorded';
        return [
          'ASK A VET™ Animal Poison & Toxin Summary',
          `Species: ${spName}`,
          `Weight: ${state.weight?`${fmt(state.weight)} ${state.weightUnit}${kg?` (${fmt(kg)} kg)`:''}`:'Not recorded'}`,
          `Toxin: ${t.name}`,
          `Product / brand: ${state.productName||'Not recorded'}`,
          `Exposure route: ${routeLabel()}`,
          `Exposure: ${plainAmount(t,d)}`,
          `Time since exposure: ${timeLabel||'Not recorded'}`,
          `Amount confidence: ${state.certainty}`,
          `Symptoms now: ${state.symptomatic?'Yes':'No / not recorded'}`,
          `Risk level: ${BAND_LABEL[band]}`,
          `What to do: ${state.route==='oral'?OWNER_ADVICE[band]:(routeGuidance(state.route)?.body||OWNER_ADVICE[band])}`,
          '',
          'Decision support only, not a diagnosis. Contact a veterinarian or animal poison service if worried.'
        ].join('\n');
      }
      function escapeHtml(value){return String(value??'').replace(/[&<>'"]/g,ch=>({'&':'&amp;','<':'&lt;','>':'&gt;',"'":'&#39;','"':'&quot;'}[ch]));}
      async function copyText(text){
        if(navigator.clipboard&&window.isSecureContext){await navigator.clipboard.writeText(text);return;}
        const ta=document.createElement('textarea');ta.value=text;ta.style.position='fixed';ta.style.opacity='0';document.body.appendChild(ta);ta.select();document.execCommand('copy');ta.remove();
      }
      function resetCalculator(){
        state.species=null; state.weight=null; state.weightUnit='kg'; state.toxin=null; resetExposureState();
        weightInput.value=''; searchBox.value='';
        document.querySelectorAll('#speciesGrid button').forEach(b=>b.classList.remove('active'));
        document.querySelectorAll('#weightUnit button').forEach(b=>b.classList.toggle('active',b.dataset.u==='kg'));
        document.getElementById('weightHint').textContent="Enter the animal's current weight.";
        renderToxList(); renderExposure(); renderResult(); updateProgress();
        document.getElementById('stepSpecies')?.scrollIntoView({behavior:'smooth',block:'start'});
      }
      function wireActions(t,sp,band,d){
        const bp=document.getElementById('btnPrint');
        if(bp) bp.onclick=()=>printSummary(t,sp,band,d);
        const bc=document.getElementById('btnCopy');
        if(bc) bc.onclick=async()=>{const status=document.getElementById('actionStatus');try{await copyText(summaryText(t,band,d));status.textContent='Summary copied.';}catch{status.textContent='Could not copy automatically. Use Print / save PDF instead.';}};
        const be=document.getElementById('btnEmail');
        if(be) be.onclick=()=>{const subject=encodeURIComponent(`Animal toxin exposure: ${t.name}`);const body=encodeURIComponent(summaryText(t,band,d));window.location.href=`mailto:?subject=${subject}&body=${body}`;};
        const br=document.getElementById('btnReset');
        if(br) br.onclick=resetCalculator;
        const bv=document.getElementById('btnVet');
        if(bv) bv.onclick=()=>{ state.showVet=true; renderResult(); document.querySelector('.vetpanel')?.scrollIntoView({behavior:'smooth',block:'nearest'}); };
        const bvc=document.getElementById('btnVetClose');
        if(bvc) bvc.onclick=()=>{ state.showVet=false; renderResult(); };
      }
      
      /* ---- printable emergency summary ---- */
      function printSummary(t,sp,band,d){
        const spName=SPECIES.find(s=>s.id===state.species).name;
        const kg=weightKg();
        const timeLabel = state.timeSince? (TIME_OPTS.find(o=>o.id===state.timeSince)||{}).label : 'Not recorded';
        const bandColors={safe:'#2f9e5f',mild:'#c9a227',moderate:'#e08b1e',severe:'#e0533b',critical:'#c0243a'};
        let amountStr='Any exposure treated as significant';
        if(state.route!=='oral') amountStr='Non-oral exposure: oral dose not calculated';
        if(!sp.qualitative && d){
          const parts=[];
          (t.inputs||[]).forEach(f=>{
            if(state.inputs[f.id]!=null){
              let val=state.inputs[f.id];
              if(f.type==='select'){ const o=f.options.find(o=>parseFloat(o.value)===val); val=o?o.label:val; }
              parts.push(`${f.label}: ${val}`);
            }
          });
          amountStr = parts.join(' · ') + ` → <b>${fmt(d.value)} ${d.unit}</b>`;
        }
        const advice = state.route==='oral'?OWNER_ADVICE[band]:(routeGuidance(state.route)?.body||OWNER_ADVICE[band]);
        const isEmerg = band==='critical'||band==='severe';
        const sheet=document.getElementById('printSheet');
        sheet.innerHTML=`
          <h1>Pet Poisoning Emergency Summary</h1>
          <div class="psub">Generated by the ASK A VET™ General Toxins Calculator · by Dr Duncan Houston · decision support only, not a diagnosis</div>
          ${isEmerg?'<div class="pcrit">TREAT AS AN EMERGENCY: CONTACT A VET IMMEDIATELY</div>':''}
          <div class="prow"><div class="k">Species</div><div>${spName}</div></div>
          <div class="prow"><div class="k">Body weight</div><div>${state.weight?state.weight+' '+state.weightUnit+(kg?` (${fmt(kg)} kg)`:''):':'}</div></div>
          <div class="prow"><div class="k">Toxin</div><div>${t.name}</div></div>
          <div class="prow"><div class="k">Product / brand</div><div>${state.productName?escapeHtml(state.productName):':'}</div></div>
          <div class="prow"><div class="k">Exposure route</div><div>${routeLabel()}</div></div>
          <div class="prow"><div class="k">Amount / exposure</div><div>${amountStr}</div></div>
          <div class="prow"><div class="k">Time since exposure</div><div>${timeLabel}</div></div>
          <div class="prow"><div class="k">Amount confidence</div><div>${state.certainty}</div></div>
          <div class="prow"><div class="k">Symptoms now</div><div>${state.symptomatic?'Yes':'No / not recorded'}</div></div>
          <div class="prow"><div class="k">Risk level</div><div><span class="pband" style="background:${bandColors[band]}">${BAND_LABEL[band].toUpperCase()}</span></div></div>
          <div class="pblock"><h2>What to do</h2><p>${advice}</p></div>
          ${sp.onset?`<div class="pblock"><h2>Expected onset</h2><p>${sp.onset}</p></div>`:''}
          ${sp.treat?`<div class="pblock"><h2>Clinical notes for the vet</h2><p>${(t.active?'Active principle: '+t.active+'. ':'')}${sp.treat}${sp.lethal?' Lethal/high-risk: '+sp.lethal+'.':''}</p></div>`:''}
          <div class="pblock"><h2>To fill in</h2>
            <div class="prow"><div class="k">Time eaten (clock time)</div><div class="fill"></div></div>
            <div class="prow"><div class="k">Product packaging available?</div><div class="fill"></div></div>
            <div class="prow"><div class="k">Vet / clinic phone</div><div class="fill"></div></div>
            <div class="prow"><div class="k">Time you called</div><div class="fill"></div></div>
          </div>
          <div class="pfoot">Take this sheet and the product packaging to your vet. Doses are rounded triage thresholds, not exact lethal doses. ASK A VET™: Talk. Track. Trust.</div>
        `;
        window.print();
      }
      
      function fmt(n){ if(!Number.isFinite(n))return ':'; if(n>=100)return String(Math.round(n)); if(n>=10)return n.toFixed(1); if(n>=1)return n.toFixed(2); return n.toFixed(3); }
      
      function thresholdTable(t,sp){
        if(!sp.bands)return '';
        let rows='';
        sp.bands.forEach(b=>{ rows+=`<tr><td>&ge; ${b.min}</td><td>${BAND_LABEL[b.band]}<span class="pill ${bandClass(b.band)}">${b.band}</span></td></tr>`; });
        return `<table><tr><td><b>Dose (${t.doseUnit||'per kg'})</b></td><td><b>Band</b></td></tr>${rows}</table>`;
      }
      
      function clinicalBlock(t,sp,d){
        if(state.mode!=='clinical')return '';
        let html=`<div class="clinical"><h3>Clinical notes: ${SPECIES.find(s=>s.id===state.species).name}</h3>`;
        if(t.active) html+=`<div style="font-size:12.5px;color:var(--muted)">Active principle: <b style="color:var(--ink)">${t.active}</b></div>`;
        html+=`<table>`;
        if(sp.onset) html+=`<tr><td>Onset</td><td>${sp.onset}</td></tr>`;
        if(sp.lethal) html+=`<tr><td>Lethal / high-risk</td><td>${sp.lethal}</td></tr>`;
        if(sp.treat) html+=`<tr><td>Management</td><td>${sp.treat}</td></tr>`;
        if(sp.note) html+=`<tr><td>Notes</td><td>${sp.note}</td></tr>`;
        html+=`</table>`;
        if(sp.bands) html+=thresholdTable(t,sp);
        html+=`</div>`;
        return html;
      }
      function ownerNoteBlock(t){
        if(state.mode!=='owner'||!t.ownerNote)return '';
        return `<div class="clinical"><h3>Dr Duncan's note</h3><div>${t.ownerNote}</div></div>`;
      }
      
      /* init */
      renderToxList();
      renderExposure();
      renderResult();
      updateProgress();
    }
  }

  if (!customElements.get("s-general-toxins-calculator")) {
    customElements.define("s-general-toxins-calculator", GeneralToxinsCalculator);
  }
})();
